import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Menu,
  MenuItem,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import UnfoldMoreOutlinedIcon from "@mui/icons-material/UnfoldMoreOutlined";
import UnfoldLessOutlinedIcon from "@mui/icons-material/UnfoldLessOutlined";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useTranslation } from "react-i18next";
import MeetingMediaHelpDialog from "./MeetingMediaHelpDialog";
import MeetingDeleteMediaDialog from "./MeetingDeleteMediaDialog";
import {
  meetingMediaUrl,
  meetingPhotoDisplayUrl,
  meetingPhotoThumbUrl,
} from "../../api/Meetings/meetingSessionsApi";
import {
  meetingLinkedSampleHintName,
  meetingLinkedSampleNames,
  meetingPhotoDownloadName,
  meetingPhotoIsLinked,
} from "./meetingLinkedSampleLabel";
import {
  cachedMeetingPhotoFile,
  copyMeetingPhotoFileToClipboard,
  fetchMeetingPhotoAsFile,
  fileFromLoadedImage,
  isSafariBrowser,
  revokeMeetingDragBlobUrls,
  startMeetingPhotoDrag,
} from "./meetingPhotoDrag";
import {
  applyMeetingBoardDrag,
  buildMeetingMediaBoard,
  canRemoveMeetingMediaGroup,
  mediaGroupsFromBoard,
  mediaKindFromMime,
  isMeetingPdfPhoto,
  appendMeetingMediaGroup,
  meetingAlbumInsertIndex,
  meetingAlbumStackPhotoIds,
  moveMeetingBoardGroup,
  moveMeetingBoardPhoto,
  moveMeetingBoardPhotos,
  newMeetingMediaGroupId,
} from "../../utils/meetingRecorder";
import {
  meetingMediaGroupCardSx,
  meetingMediaGroupsFeedSx,
  meetingMediaThumbMediaSx,
  meetingMediaThumbWrapSx,
  meetingHeaderToolGroupSx,
  meetingHeaderToolGroupLabelSx,
  meetingHeaderToolIconBtnSx,
  meetingSelectionBarSx,
  MEETING_CARD_RADIUS,
  meetingAlbumGroupSurfaceSx,
  meetingAlbumUngroupedSurfaceSx,
} from "./meetingUiStyles";
import MeetingVideoThumb from "./MeetingVideoThumb";
import MeetingMediaTeamNoteEditor from "./MeetingMediaTeamNoteEditor";

const stopDragEvent = (e) => {
  e.stopPropagation();
};

function photoLeftWithClient(photo) {
  return /se queda/i.test(String(photo?.caption || photo?.teamNote || ""));
}

/** Arrastre nativo: el archivo sale a Illustrator/escritorio y también se lee aquí. */
export const MEETING_PHOTO_DRAG_TYPE = "application/x-meeting-photo";

function canDragPhotoToDesktop() {
  return true;
}

function dragTypes(event) {
  return Array.from(event?.dataTransfer?.types || []);
}

/** Payload del arrastre nativo en curso (dragover a veces no enseña el tipo custom). */
let activeMeetingPhotoDrag = null;
let meetingDragHandled = false;
/** PNG copiado al arrastrar (puente para Illustrator vía Ctrl+V). */
let meetingPhotoClipboardPrimed = false;
/** Arrastre desde el icono Copiar → al soltar recordamos Ctrl+V. */
let meetingPhotoCopyIconDrag = false;

function clearActiveMeetingPhotoDrag() {
  activeMeetingPhotoDrag = null;
  meetingDragHandled = false;
}

function isMeetingPhotoDragEvent(event) {
  const types = dragTypes(event);
  if (types.includes(MEETING_PHOTO_DRAG_TYPE)) return true;
  // En dragover Chrome solo enseña "Files" si metimos un File; el flag
  // en memoria marca nuestro gesto. Nunca lo uses si no hay gesto activo.
  return Boolean(activeMeetingPhotoDrag);
}

if (typeof window !== "undefined" && !window.__meetingPhotoDragEndBound) {
  window.__meetingPhotoDragEndBound = true;
  window.addEventListener(
    "dragend",
    (event) => {
      const handled = meetingDragHandled;
      const photoId = activeMeetingPhotoDrag?.id;
      const dropEffect = event?.dataTransfer?.dropEffect || "none";
      const clipboardPrimed = meetingPhotoClipboardPrimed;
      const fromCopyIcon = meetingPhotoCopyIconDrag;
      meetingPhotoClipboardPrimed = false;
      meetingPhotoCopyIconDrag = false;
      clearActiveMeetingPhotoDrag();
      revokeMeetingDragBlobUrls();
      window.dispatchEvent(
        new CustomEvent("meeting-photo-drag-end", {
          detail: {
            handled,
            photoId,
            dropEffect,
            clipboardPrimed,
            fromCopyIcon,
          },
        }),
      );
    },
    true,
  );
}

function AlbumThumb({
  photo,
  selected,
  onToggleSelect,
  openMedia,
  onRequestDelete,
  onCopyPhoto,
  onStartDrag,
  onOsDragStart,
  onCopyIconDragStart,
  onPrefetchPhoto,
  onNativeDragOverPhoto,
  onNativeDragLeavePhoto,
  stackHighlight = false,
  t,
  theme,
  loadMedia = true,
}) {
  const rootRef = useRef(null);
  const copyDraggedRef = useRef(false);
  const [inView, setInView] = useState(false);
  const video =
    photo.kind === "video" || mediaKindFromMime(photo.mimeType) === "video";
  const pdf = isMeetingPdfPhoto(photo);
  const stackGreen = theme.palette.success.main;

  useEffect(() => {
    if (!loadMedia || pdf) return undefined;
    const el = rootRef.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: "120px 0px", threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loadMedia, pdf, photo?.id]);

  const thumbSrc = pdf ? "" : meetingPhotoThumbUrl(photo);
  const showImage = !pdf && loadMedia && inView && Boolean(thumbSrc);
  const osDrag = !video && !pdf && canDragPhotoToDesktop();
  const safariDrag = osDrag && isSafariBrowser();
  const dragSrc = safariDrag
    ? meetingPhotoDisplayUrl(photo) || thumbSrc
    : thumbSrc;

  return (
    <Box
      ref={rootRef}
      data-meeting-photo-id={photo.id}
      className="meeting-album-thumb"
      title={osDrag ? t("Meetings.mediaDragToDesktop") : undefined}
      draggable={osDrag}
      onPointerEnter={() => {
        if (osDrag) onPrefetchPhoto?.(photo);
      }}
      onPointerDown={(e) => {
        if (e.target.closest("button, input, label")) return;
        e.currentTarget.dataset.px = String(e.clientX);
        e.currentTarget.dataset.py = String(e.clientY);
        if (osDrag) onPrefetchPhoto?.(photo);
        onStartDrag?.(e);
      }}
      onPointerUp={(e) => {
        if (e.target.closest("button, input, label")) return;
        if (e.currentTarget.dataset.dragged === "1") {
          delete e.currentTarget.dataset.dragged;
          return;
        }
        const x = Number(e.currentTarget.dataset.px);
        const y = Number(e.currentTarget.dataset.py);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return;
        const moved = Math.abs(e.clientX - x) + Math.abs(e.clientY - y);
        // Abrir visor: el padre cancela el gesto pointer para no dejar el drag muerto.
        if (moved < 8) openMedia?.(photo.id);
      }}
      onDragStart={(e) => {
        if (!osDrag) {
          e.preventDefault();
          return;
        }
        if (e.target.closest("button, input, label")) {
          e.preventDefault();
          return;
        }
        e.stopPropagation();
        e.currentTarget.dataset.dragged = "1";
        onOsDragStart?.(e);
      }}
      onDragEnd={() => {
        onNativeDragLeavePhoto?.();
        const root = rootRef.current;
        if (root) {
          root.style.opacity = "";
          window.setTimeout(() => {
            if (root) delete root.dataset.dragged;
          }, 250);
        }
      }}
      onDragOver={(e) => {
        if (!isMeetingPhotoDragEvent(e)) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
        onNativeDragOverPhoto?.(photo.id);
      }}
      onDragLeave={(e) => {
        if (!isMeetingPhotoDragEvent(e)) return;
        const related = e.relatedTarget;
        if (related && e.currentTarget.contains(related)) return;
        onNativeDragLeavePhoto?.(photo.id);
      }}
      sx={{
        position: "relative",
        minWidth: 0,
        aspectRatio: "1 / 1",
        overflow: stackHighlight ? "visible" : "hidden",
        borderRadius: 2,
        bgcolor: pdf
          ? alpha(theme.palette.error.main, 0.08)
          : video
            ? "grey.900"
            : "background.paper",
        cursor: pdf ? "pointer" : "grab",
        touchAction: osDrag ? "auto" : "none",
        transition: "transform 0.12s ease, box-shadow 0.12s ease",
        outline:
          selected && !stackHighlight
            ? `2px solid ${theme.palette.primary.main}`
            : "none",
        outlineOffset: -2,
        transform: stackHighlight ? "scale(1.06)" : "none",
        zIndex: stackHighlight ? 5 : "auto",
        "&:hover .meeting-album-thumb-select, &:focus-within .meeting-album-thumb-select":
          {
            opacity: 1,
          },
        "@media (hover: hover)": {
          "&:hover .meeting-album-thumb-delete, &:focus-within .meeting-album-thumb-delete, &:hover .meeting-album-thumb-copy, &:focus-within .meeting-album-thumb-copy":
            {
              opacity: 1,
            },
        },
        "@media (hover: none)": {
          "& .meeting-album-thumb-select": { opacity: 1 },
          "& .meeting-album-thumb-copy": { opacity: 1 },
        },
      }}
    >
      {stackHighlight ? (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: -5,
            borderRadius: 1.75,
            border: `3px solid ${stackGreen}`,
            bgcolor: alpha(stackGreen, 0.28),
            boxShadow: `0 0 0 4px ${alpha(stackGreen, 0.45)}, 0 10px 24px ${alpha(
              stackGreen,
              0.55,
            )}`,
            pointerEvents: "none",
            zIndex: 6,
          }}
        />
      ) : null}
      <Box
        sx={{
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          borderRadius: 2,
          overflow: "hidden",
          position: "relative",
          zIndex: 1,
          width: "100%",
          height: "100%",
        }}
      >
        {pdf ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "grid",
              placeItems: "center",
              color: "error.main",
            }}
          >
            <PictureAsPdfOutlinedIcon sx={{ fontSize: 36 }} />
          </Box>
        ) : null}
        {showImage ? (
          <Box
            component="img"
            src={dragSrc}
            alt=""
            loading="lazy"
            decoding="async"
            draggable={false}
            onLoad={() => onPrefetchPhoto?.(photo)}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              pointerEvents: "none",
            }}
          />
        ) : null}
        {video ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: showImage ? "transparent" : "grey.900",
            }}
          >
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                bgcolor: "rgba(0,0,0,0.45)",
                color: "common.white",
                display: "grid",
                placeItems: "center",
                fontSize: 11,
                fontWeight: 700,
                pl: 0.2,
              }}
            >
              ▶
            </Box>
          </Box>
        ) : null}
      </Box>
      <Checkbox
        size="small"
        className="meeting-album-thumb-ctrl meeting-album-thumb-select"
        checked={selected}
        onChange={() => onToggleSelect?.(photo.id)}
        onClick={stopDragEvent}
        onMouseDown={stopDragEvent}
        onTouchStart={stopDragEvent}
        onPointerDown={stopDragEvent}
        inputProps={{ "aria-label": t("Meetings.selectMediaItem") }}
        sx={{
          position: "absolute",
          top: 2,
          left: 2,
          zIndex: 2,
          p: 0.15,
          opacity: selected ? 1 : 0,
          transition: "opacity 0.12s ease",
          bgcolor: alpha(theme.palette.common.white, 0.88),
          borderRadius: 0.5,
        }}
      />
      {osDrag && (onCopyPhoto || onCopyIconDragStart) ? (
        <IconButton
          size="small"
          className="meeting-album-thumb-ctrl meeting-album-thumb-copy"
          aria-label={t("Meetings.mediaCopyForIllustrator")}
          title={t("Meetings.mediaCopyDragHint")}
          draggable
          onClick={(e) => {
            stopDragEvent(e);
            if (copyDraggedRef.current) {
              copyDraggedRef.current = false;
              return;
            }
            onCopyPhoto?.(photo);
          }}
          onMouseDown={stopDragEvent}
          onTouchStart={stopDragEvent}
          onPointerDown={(e) => {
            stopDragEvent(e);
            onPrefetchPhoto?.(photo);
          }}
          onDragStart={(e) => {
            e.stopPropagation();
            copyDraggedRef.current = true;
            if (rootRef.current) rootRef.current.dataset.dragged = "1";
            if (onCopyIconDragStart) onCopyIconDragStart(e, photo);
            else onOsDragStart?.(e);
          }}
          onDragEnd={() => {
            window.setTimeout(() => {
              copyDraggedRef.current = false;
            }, 200);
          }}
          sx={{
            position: "absolute",
            bottom: 2,
            right: 2,
            zIndex: 2,
            p: 0.2,
            opacity: 0,
            transition: "opacity 0.12s ease",
            color: "common.white",
            cursor: "grab",
            bgcolor: alpha(theme.palette.common.black, 0.5),
            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.95) },
            "&:active": { cursor: "grabbing" },
          }}
        >
          <ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />
        </IconButton>
      ) : null}
      {onRequestDelete ? (
        <IconButton
          size="small"
          className="meeting-album-thumb-ctrl meeting-album-thumb-delete"
          aria-label={t("Meetings.deleteMedia")}
          onClick={(e) => {
            stopDragEvent(e);
            onRequestDelete(photo);
          }}
          onMouseDown={stopDragEvent}
          onTouchStart={stopDragEvent}
          onPointerDown={stopDragEvent}
          sx={{
            position: "absolute",
            top: 2,
            right: 2,
            zIndex: 2,
            p: 0.15,
            opacity: selected ? 1 : 0,
            transition: "opacity 0.12s ease",
            color: "common.white",
            bgcolor: alpha(theme.palette.common.black, 0.45),
            "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.9) },
          }}
        >
          <DeleteOutlineOutlinedIcon sx={{ fontSize: 14 }} />
        </IconButton>
      ) : null}
      {photoLeftWithClient(photo) ? (
        <Box
          title={t("Meetings.leaveSamplesBadge")}
          aria-label={t("Meetings.leaveSamplesBadge")}
          sx={{
            position: "absolute",
            bottom: 6,
            left: 6,
            zIndex: 2,
            width: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor: "info.main",
            border: "2px solid",
            borderColor: "common.white",
            boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
            pointerEvents: "none",
          }}
        />
      ) : null}
    </Box>
  );
}

const AlbumThumbMemo = React.memo(AlbumThumb);

const COLLAPSE_WHEN_NAMED = 6;

function MediaCard({
  photo,
  provided,
  isDragging,
  notesById,
  audioByPhotoId,
  summaryLang = "es",
  highlightPhotoId,
  expandedNoteIds,
  setExpandedNoteIds,
  openMedia,
  summary: _summary,
  onSaveTeamNote,
  onRequestDelete,
  selected,
  onToggleSelect,
  onOpenLinkedSample,
  compact = false,
  t,
  theme,
}) {
  const isVideo =
    photo.kind === "video" || mediaKindFromMime(photo.mimeType) === "video";
  const highlighted = Number(highlightPhotoId) === Number(photo.id);
  const bucket = notesById.get(Number(photo.id));
  const audioLink = audioByPhotoId.get(Number(photo.id));
  const preferTranslatedVisual = summaryLang === "en";
  // EN: no mezclar Whisper ES en fotos; el acta EN va en visualNotes.
  const visual = isVideo || preferTranslatedVisual ? bucket?.visual || [] : [];
  const spoken = [
    ...(bucket?.spoken || []),
    ...(!isVideo && !preferTranslatedVisual ? audioLink?.spoken || [] : []),
  ];
  const linked = bucket?.linked || [];
  const hasNotes = visual.length > 0 || spoken.length > 0 || linked.length > 0;
  const expanded = expandedNoteIds.has(Number(photo.id));
  const joinTexts = (arr) => arr.join("\n");
  const truncate = (text) => {
    if (text.length <= 140) return text;
    return `${text.slice(0, 140).trim()}…`;
  };
  const previewSource = spoken.length ? spoken : visual;
  const previewLabelKey = spoken.length
    ? "Meetings.mediaNoteSpoken"
    : "Meetings.mediaNoteVisual";
  const audioSeq =
    audioLink?.chunk?.seq != null ? Number(audioLink.chunk.seq) + 1 : null;

  const sourceText = String(
    isVideo ? photo.transcriptText : photo.visionText || "",
  ).trim();
  const sourceLabelKey = isVideo
    ? "Meetings.mediaTranscript"
    : "Meetings.mediaVisionRead";

  const noteSx = {
    whiteSpace: "pre-wrap",
    lineHeight: 1.45,
    overflowWrap: "anywhere",
    wordBreak: "normal",
    fontSize: "0.8125rem",
    color: "text.secondary",
  };

  return (
    <Box
      ref={provided.innerRef}
      {...provided.draggableProps}
      data-meeting-photo-id={photo.id}
      sx={{
        ...meetingMediaGroupCardSx(theme, {
          selected,
          highlighted,
          compact,
        }),
        opacity: isDragging ? 0.35 : 1,
      }}
    >
      <Box
        {...provided.dragHandleProps}
        sx={{
          position: "relative",
          minWidth: 0,
          alignSelf: "start",
          cursor: isDragging ? "grabbing" : "grab",
          touchAction: "none",
        }}
      >
        <Checkbox
          size="small"
          checked={selected}
          onChange={() => onToggleSelect?.(photo.id)}
          onClick={stopDragEvent}
          onMouseDown={stopDragEvent}
          onTouchStart={stopDragEvent}
          inputProps={{
            "aria-label": t("Meetings.selectMediaItem"),
          }}
          sx={{
            position: "absolute",
            top: 4,
            left: 4,
            zIndex: 2,
            p: 0.35,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.common.white, 0.92),
            boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
            "&:hover": {
              bgcolor: alpha(theme.palette.common.white, 1),
            },
          }}
        />
        <Box
          onClick={() => openMedia(photo.id)}
          sx={{
            ...meetingMediaThumbWrapSx,
            ...(compact
              ? {
                  aspectRatio: "1 / 1",
                  borderRadius: 1.25,
                }
              : null),
          }}
        >
          {isVideo ? (
            <MeetingVideoThumb
              fileUrl={photo.displayFileUrl || photo.fileUrl}
              sx={{ width: "100%", height: "100%", aspectRatio: "unset" }}
            />
          ) : (
            <Box
              component="img"
              src={meetingPhotoDisplayUrl(photo)}
              alt={photo.caption || ""}
              loading="lazy"
              draggable={false}
              sx={meetingMediaThumbMediaSx}
              onError={(e) => {
                const fallback = photo.muestraFrontUrl
                  ? meetingMediaUrl(photo.muestraFrontUrl)
                  : "";
                if (fallback && e?.currentTarget?.src !== fallback) {
                  e.currentTarget.src = fallback;
                }
              }}
            />
          )}
          <Chip
            size="small"
            label={photo.pendingUpload ? "…" : `#${photo.id}`}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              height: 22,
              fontWeight: 800,
              bgcolor: "rgba(0,0,0,0.72)",
              color: "#fff",
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
          {photo.aiStatus === "pending" || photo.aiStatus === "error" ? (
            <Chip
              size="small"
              color={photo.aiStatus === "error" ? "error" : "warning"}
              label={
                photo.aiStatus === "error"
                  ? t("Meetings.mediaAiError")
                  : t("Meetings.mediaAiPending")
              }
              title={photo.aiError || undefined}
              sx={{
                position: "absolute",
                top: 34,
                right: 8,
                height: 20,
                fontWeight: 700,
                "& .MuiChip-label": { px: 0.55 },
              }}
            />
          ) : null}
          {String(photo.teamNote || "").trim() ? (
            <Chip
              size="small"
              icon={<NotesOutlinedIcon sx={{ fontSize: "14px !important" }} />}
              label={t("Meetings.teamNoteShort")}
              sx={{
                position: "absolute",
                bottom: 8,
                left: 8,
                height: 22,
                fontWeight: 800,
                bgcolor: alpha(theme.palette.common.white, 0.94),
                "& .MuiChip-label": { px: 0.55 },
                "& .MuiChip-icon": { ml: 0.5, color: "success.main" },
              }}
            />
          ) : null}
          {onRequestDelete ? (
            <IconButton
              size="small"
              aria-label={t("Meetings.deleteMedia")}
              onClick={(e) => {
                stopDragEvent(e);
                onRequestDelete(photo);
              }}
              onMouseDown={stopDragEvent}
              onTouchStart={stopDragEvent}
              sx={{
                position: "absolute",
                bottom: 8,
                right: 8,
                zIndex: 2,
                p: 0.4,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.error.main, 0.92),
                color: "common.white",
                boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                "&:hover": {
                  bgcolor: "error.dark",
                },
              }}
            >
              <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          ) : null}
        </Box>
      </Box>

      {!compact ? (
        <Box
          sx={{
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            userSelect: "text",
            cursor: "auto",
          }}
          onMouseDown={stopDragEvent}
          onTouchStart={stopDragEvent}
        >
          {(() => {
            const links =
              Array.isArray(photo.linkedMuestras) && photo.linkedMuestras.length
                ? photo.linkedMuestras
                : photo.idMuestra && photo.muestraNombre
                  ? [
                      {
                        idMuestra: photo.idMuestra,
                        muestraNombre: photo.muestraNombre,
                      },
                    ]
                  : [];
            const leftWith = photoLeftWithClient(photo);
            if (!links.length && !leftWith) return null;
            return (
              <Stack
                direction="row"
                flexWrap="wrap"
                useFlexGap
                spacing={0.5}
                sx={{ mb: 0.5 }}
              >
                {links.map((link) => {
                  const names = meetingLinkedSampleNames(link);
                  const chips = [];
                  if (names.cliente) {
                    chips.push(
                      <Chip
                        key={`${link.idMuestra}-cliente`}
                        size="small"
                        color="primary"
                        label={names.cliente}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenLinkedSample?.(link.idMuestra);
                        }}
                        onMouseDown={stopDragEvent}
                        onTouchStart={stopDragEvent}
                        sx={{
                          height: 22,
                          fontWeight: 800,
                          maxWidth: "100%",
                          "& .MuiChip-label": {
                            px: 0.75,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        }}
                        title={t("Meetings.linkedSampleHint", {
                          name: meetingLinkedSampleHintName(link),
                        })}
                      />,
                    );
                  }
                  if (names.samp && names.samp !== names.cliente) {
                    chips.push(
                      <Chip
                        key={`${link.idMuestra}-samp`}
                        size="small"
                        color={names.cliente ? "default" : "primary"}
                        variant={names.cliente ? "outlined" : "filled"}
                        label={names.samp}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenLinkedSample?.(link.idMuestra);
                        }}
                        onMouseDown={stopDragEvent}
                        onTouchStart={stopDragEvent}
                        sx={{
                          height: 22,
                          fontWeight: 800,
                          maxWidth: "100%",
                          "& .MuiChip-label": {
                            px: 0.75,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        }}
                        title={t("Meetings.linkedSampleHint", {
                          name: meetingLinkedSampleHintName(link),
                        })}
                      />,
                    );
                  }
                  if (!chips.length && names.fallback) {
                    chips.push(
                      <Chip
                        key={`${link.idMuestra}-id`}
                        size="small"
                        color="primary"
                        label={names.fallback}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenLinkedSample?.(link.idMuestra);
                        }}
                        onMouseDown={stopDragEvent}
                        onTouchStart={stopDragEvent}
                        sx={{
                          height: 22,
                          fontWeight: 800,
                        }}
                      />,
                    );
                  }
                  return chips;
                })}
                {leftWith ? (
                  <Chip
                    size="small"
                    color="info"
                    label={t("Meetings.leaveSamplesBadge")}
                    onMouseDown={stopDragEvent}
                    onTouchStart={stopDragEvent}
                    sx={{ height: 22, fontWeight: 800 }}
                  />
                ) : null}
              </Stack>
            );
          })()}

          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 0.35 }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: "text.secondary",
                letterSpacing: 0.2,
              }}
            >
              {isVideo ? t("Meetings.video") : t("Meetings.photo")}
            </Typography>
            {photo.offsetLabel ? (
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
              >
                {photo.offsetLabel}
              </Typography>
            ) : null}
            {String(photo.uploadedByUsername || "").trim() ? (
              <Chip
                size="small"
                variant="outlined"
                label={t("Meetings.mediaUploadedBy", {
                  name: String(photo.uploadedByUsername).trim(),
                })}
                sx={{
                  height: 20,
                  fontWeight: 700,
                  maxWidth: "100%",
                  "& .MuiChip-label": {
                    px: 0.7,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            ) : null}
            {!isVideo && audioSeq != null ? (
              <Chip
                size="small"
                variant="outlined"
                color="primary"
                label={t("Meetings.photoLinkedAudio", { n: audioSeq })}
                sx={{
                  height: 20,
                  fontWeight: 700,
                  "& .MuiChip-label": { px: 0.7 },
                }}
              />
            ) : null}
          </Stack>

          {hasNotes ? (
            <Box sx={{ mt: 0.25 }}>
              <Button
                size="small"
                onMouseDown={stopDragEvent}
                onTouchStart={stopDragEvent}
                onClick={() => {
                  setExpandedNoteIds((prev) => {
                    const next = new Set(prev);
                    const idNum = Number(photo.id);
                    if (next.has(idNum)) next.delete(idNum);
                    else next.add(idNum);
                    return next;
                  });
                }}
                endIcon={
                  expanded ? (
                    <ExpandLessOutlinedIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <ExpandMoreOutlinedIcon sx={{ fontSize: 16 }} />
                  )
                }
                sx={{
                  px: 0,
                  minWidth: 0,
                  textTransform: "none",
                  fontWeight: 700,
                  color: "text.secondary",
                  justifyContent: "flex-start",
                }}
              >
                {t(previewLabelKey)}
                {!expanded && previewSource.length
                  ? `: ${truncate(joinTexts(previewSource)).slice(0, 48)}${
                      joinTexts(previewSource).length > 48 ? "…" : ""
                    }`
                  : ""}
              </Button>
              {expanded ? (
                <Stack spacing={0.65} sx={{ mt: 0.35, pl: 0.25 }}>
                  {spoken.length ? (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: "text.secondary",
                          letterSpacing: 0.2,
                          textTransform: "uppercase",
                          fontSize: "0.65rem",
                        }}
                      >
                        {t("Meetings.mediaNoteSpoken")}
                      </Typography>
                      <Typography variant="body2" sx={noteSx}>
                        {joinTexts(spoken)}
                      </Typography>
                    </Box>
                  ) : null}
                  {visual.length ? (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: "text.secondary",
                          letterSpacing: 0.2,
                          textTransform: "uppercase",
                          fontSize: "0.65rem",
                        }}
                      >
                        {t("Meetings.mediaNoteVisual")}
                      </Typography>
                      <Typography variant="body2" sx={noteSx}>
                        {joinTexts(visual)}
                      </Typography>
                    </Box>
                  ) : null}
                  {linked.length ? (
                    <Typography variant="body2" sx={noteSx}>
                      {joinTexts(linked)}
                    </Typography>
                  ) : null}
                </Stack>
              ) : null}
            </Box>
          ) : null}

          {sourceText ? (
            <Box
              sx={{ mt: 0.75 }}
              onMouseDown={stopDragEvent}
              onTouchStart={stopDragEvent}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  letterSpacing: 0.2,
                  textTransform: "uppercase",
                  fontSize: "0.65rem",
                }}
              >
                {t(sourceLabelKey)}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  ...noteSx,
                  mt: 0.25,
                  maxHeight: 160,
                  overflow: "auto",
                  px: 1,
                  py: 0.75,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.text.primary, 0.04),
                }}
              >
                {sourceText}
              </Typography>
            </Box>
          ) : null}

          <MeetingMediaTeamNoteEditor
            photo={photo}
            onSaveTeamNote={onSaveTeamNote}
            t={t}
          />
        </Box>
      ) : null}
    </Box>
  );
}

function isOsFileDragEvent(event) {
  const types = dragTypes(event);
  if (!types.includes("Files")) return false;
  // Drop del SO / galería. Si traemos tipo custom, es rearrange interno.
  if (types.includes(MEETING_PHOTO_DRAG_TYPE)) return false;
  if (activeMeetingPhotoDrag) return false;
  return true;
}

function isMeetingDropMediaFile(file) {
  if (!file) return false;
  const type = String(file.type || "").toLowerCase();
  if (
    type.startsWith("image/") ||
    type.startsWith("video/") ||
    type === "application/pdf"
  ) {
    return true;
  }
  return /\.(jpe?g|png|gif|webp|heic|heif|bmp|mov|mp4|m4v|webm|avi|pdf)$/i.test(
    file.name || "",
  );
}

export default forwardRef(function MeetingMediaGroupsFeed(
  {
    photos,
    mediaGroups,
    notesById,
    audioByPhotoId,
    summaryLang = "es",
    highlightPhotoId,
    expandedNoteIds,
    setExpandedNoteIds,
    openMedia,
    summary,
    onSaveGroups,
    savingGroups = false,
    onSaveTeamNote,
    onDeleteMedia,
    onCreateSample,
    onLinkSample,
    onUnlinkSample,
    onOpenLinkedSample,
    onSelectionChange,
    /** Vista inicial: gallery (miniaturas) o cards (pastillas con notas). */
    initialLayoutMode = "gallery",
    /** Cabecera de la sesión: título, vista y filtro de autor. */
    toolbarHost = null,
    /** Subir fotos/vídeos soltados desde el SO en «Sin agrupar». */
    onDropFiles = null,
  },
  ref,
) {
  const { t } = useTranslation();
  const theme = useTheme();
  /** Móvil + iPad: cabecera compacta y bandeja fija abajo. */
  const compactChrome = useMediaQuery(theme.breakpoints.down("lg"));
  const saveTimerRef = useRef(null);
  const serverBoardKeyRef = useRef("");
  const [columns, setColumns] = useState(() =>
    buildMeetingMediaBoard(photos, mediaGroups),
  );
  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  /** "" = todos · "__none__" = sin autor · username */
  const [uploaderFilter, setUploaderFilter] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteGroupTarget, setDeleteGroupTarget] = useState(null);
  const [deletingMedia, setDeletingMedia] = useState(false);
  const [expandedGroupIds, setExpandedGroupIds] = useState(() => new Set());
  const knownGroupIdsRef = useRef(new Set());
  const [albumPages, setAlbumPages] = useState({});
  const albumPagesRef = useRef({});
  albumPagesRef.current = albumPages;
  const albumCardRefs = useRef(new Map());
  const looseDropRefs = useRef(new Map());
  const groupGestureRef = useRef(null);
  const photoGestureRef = useRef(null);
  const fileDragDepthRef = useRef(0);
  const [fileDropActive, setFileDropActive] = useState(false);
  /** Highlight nativo: zona (álbum / sin agrupar) o foto objetivo para apilar. */
  const [nativeDropUi, setNativeDropUi] = useState(null);
  const clearNativeDropUi = useCallback(() => setNativeDropUi(null), []);
  const setNativeDropZone = useCallback((zoneId, stackPhotoId = null) => {
    const z = zoneId != null ? String(zoneId) : null;
    const p = stackPhotoId != null ? String(stackPhotoId) : null;
    setNativeDropUi((prev) => {
      if (prev?.zoneId === z && prev?.stackPhotoId === p) return prev;
      return z || p ? { zoneId: z, stackPhotoId: p } : null;
    });
  }, []);
  const [illustratorPasteHint, setIllustratorPasteHint] = useState(false);
  const [copyHintSeverity, setCopyHintSeverity] = useState("info");
  const [copyHintText, setCopyHintText] = useState("");
  const lastHoveredPhotoRef = useRef(null);
  const showCopyHint = useCallback(
    (ok) => {
      setCopyHintSeverity(ok ? "success" : "warning");
      setCopyHintText(
        ok
          ? t("Meetings.mediaCopiedForIllustrator")
          : t("Meetings.mediaCopyFailed"),
      );
      setIllustratorPasteHint(true);
    },
    [t],
  );
  const copyPhotoForIllustrator = useCallback(
    async (photo) => {
      if (!photo) return false;
      const video =
        photo.kind === "video" || mediaKindFromMime(photo.mimeType) === "video";
      if (video) return false;
      let file = cachedMeetingPhotoFile(photo);
      if (!file) {
        try {
          file = await fetchMeetingPhotoAsFile(photo);
        } catch {
          file = null;
        }
      }
      if (!file) {
        showCopyHint(false);
        return false;
      }
      const ok = await copyMeetingPhotoFileToClipboard(file);
      showCopyHint(ok);
      return ok;
    },
    [showCopyHint],
  );
  useEffect(() => {
    const onEnd = (event) => {
      clearNativeDropUi();
      const detail = event?.detail || {};
      // Desde icono Copiar, o drop fallido en AI: Ctrl+V (Chrome no pega solo).
      if (
        detail.clipboardPrimed &&
        (detail.fromCopyIcon ||
          (!detail.handled && detail.dropEffect === "none"))
      ) {
        showCopyHint(true);
      }
    };
    window.addEventListener("meeting-photo-drag-end", onEnd);
    return () => window.removeEventListener("meeting-photo-drag-end", onEnd);
  }, [clearNativeDropUi, showCopyHint]);
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || String(e.key).toLowerCase() !== "c") {
        return;
      }
      const tag = String(e.target?.tagName || "").toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        e.target?.isContentEditable
      ) {
        return;
      }
      const photo = lastHoveredPhotoRef.current;
      if (!photo) return;
      e.preventDefault();
      copyPhotoForIllustrator(photo);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [copyPhotoForIllustrator]);
  const [layoutMode, setLayoutMode] = useState(() =>
    initialLayoutMode === "gallery" ? "gallery" : "cards",
  );
  const galleryMode = layoutMode === "gallery";
  const [addToGroupAnchor, setAddToGroupAnchor] = useState(null);
  const [uploaderMenuEl, setUploaderMenuEl] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  /** Alto de la bandeja «Sin agrupar» (px). Arrastrar la barra para cambiarlo. */
  const [looseDockPx, setLooseDockPx] = useState(() => {
    try {
      const n = Number(localStorage.getItem("meetingLooseDockPx"));
      if (n >= 36 && n <= 1200) return n;
    } catch {
      /* ignore */
    }
    return 132;
  });
  const looseDockPxRef = useRef(looseDockPx);
  looseDockPxRef.current = looseDockPx;

  useEffect(() => {
    if (initialLayoutMode === "gallery" || initialLayoutMode === "cards") {
      setLayoutMode(initialLayoutMode);
    }
  }, [initialLayoutMode]);

  useEffect(
    () => () => {
      groupGestureRef.current?.stop?.();
      photoGestureRef.current?.stop?.();
      if (typeof document === "undefined") return;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    },
    [],
  );

  // Galería: bandeja «sin agrupar» abierta; grupos nombrados plegados (con preview).
  useEffect(() => {
    if (!galleryMode) return;
    setExpandedGroupIds((prev) => {
      const next = new Set();
      for (const col of columnsRef.current) {
        if (col.ungrouped) next.add(String(col.id));
        else if (prev.has(String(col.id))) next.add(String(col.id));
      }
      return next;
    });
  }, [galleryMode]);

  useEffect(() => {
    const idsKey = (photos || []).map((p) => p.id).join(",");
    const linkKey = (photos || [])
      .map((p) => {
        const links =
          Array.isArray(p.linkedMuestras) && p.linkedMuestras.length
            ? p.linkedMuestras
                .map((l) => `${l.idMuestra}:${l.muestraNombre || ""}`)
                .join(",")
            : `${p.idMuestra || ""}:${p.muestraNombre || ""}`;
        // No incluir teamNote: al guardar nota no debe reconstruir el tablero (echa el foco).
        return `${p.id}:${links}:${p.uploadedByUsername || ""}`;
      })
      .join(",");
    const key = `${idsKey}|${linkKey}|${JSON.stringify(mediaGroups || [])}`;
    if (key !== serverBoardKeyRef.current) {
      serverBoardKeyRef.current = key;
      setColumns(buildMeetingMediaBoard(photos, mediaGroups));
      return;
    }
    // Misma estructura: solo refrescar teamNote en sitio.
    const noteById = new Map(
      (photos || []).map((p) => [Number(p.id), String(p.teamNote || "")]),
    );
    setColumns((prev) => {
      let changed = false;
      const next = prev.map((col) => {
        let colChanged = false;
        const photosNext = (col.photos || []).map((p) => {
          const note = noteById.get(Number(p.id));
          if (note == null || note === String(p.teamNote || "")) return p;
          colChanged = true;
          return { ...p, teamNote: note };
        });
        if (!colChanged) return col;
        changed = true;
        return { ...col, photos: photosNext };
      });
      return changed ? next : prev;
    });
  }, [photos, mediaGroups]);

  useEffect(() => {
    const alive = new Set((photos || []).map((p) => Number(p.id)));
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set();
      for (const id of prev) {
        if (alive.has(Number(id))) next.add(Number(id));
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [photos]);

  useEffect(() => {
    const ids = (columns || []).map((c) => String(c.id));
    const named = (columns || []).filter((c) => !c.ungrouped);
    if (!knownGroupIdsRef.current.size) {
      if (!ids.length) return;
      knownGroupIdsRef.current = new Set(ids);
      if (named.length >= COLLAPSE_WHEN_NAMED) {
        const ungrouped = (columns || []).find((c) => c.ungrouped);
        setExpandedGroupIds(new Set(ungrouped ? [String(ungrouped.id)] : []));
      } else {
        setExpandedGroupIds(new Set(ids));
      }
      return;
    }
    const added = ids.filter((id) => !knownGroupIdsRef.current.has(id));
    knownGroupIdsRef.current = new Set(ids);
    if (!added.length) return;
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      added.forEach((id) => next.add(id));
      return next;
    });
  }, [columns]);

  const persist = (nextColumns) => {
    const payload = mediaGroupsFromBoard(nextColumns);
    const idsKey = (photos || []).map((p) => p.id).join(",");
    // Misma forma que el effect de sync (incl. uploadedByUsername) para no
    // reconstruir el tablero con un eco viejo del servidor.
    serverBoardKeyRef.current = `${idsKey}|${(photos || [])
      .map((p) => {
        const links =
          Array.isArray(p.linkedMuestras) && p.linkedMuestras.length
            ? p.linkedMuestras
                .map((l) => `${l.idMuestra}:${l.muestraNombre || ""}`)
                .join(",")
            : `${p.idMuestra || ""}:${p.muestraNombre || ""}`;
        return `${p.id}:${links}:${p.uploadedByUsername || ""}`;
      })
      .join(",")}|${JSON.stringify(payload)}`;
    if (typeof onSaveGroups !== "function") return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      onSaveGroups(payload);
    }, 350);
  };

  const flushSaveGroups = useCallback(() => {
    if (typeof onSaveGroups !== "function") {
      return Promise.resolve(mediaGroupsFromBoard(columns));
    }
    const payload = mediaGroupsFromBoard(columns);
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    return Promise.resolve(onSaveGroups(payload)).then(() => payload);
  }, [columns, onSaveGroups]);

  const onDragEnd = (result) => {
    const pages = albumPagesRef.current || {};
    const shift = (droppableId, index) =>
      index + (Number(pages[droppableId]) || 0) * 9;
    const adjusted =
      result?.destination && result.type !== "GROUP"
        ? {
            ...result,
            source: {
              ...result.source,
              index: shift(result.source.droppableId, result.source.index),
            },
            destination: {
              ...result.destination,
              index: shift(
                result.destination.droppableId,
                result.destination.index,
              ),
            },
          }
        : result;
    const next = applyMeetingBoardDrag(columnsRef.current, adjusted);
    if (!next) return;
    columnsRef.current = next;
    setColumns(next);
    persist(next);
  };

  const removeGroup = (groupId) => {
    const idx = columns.findIndex((c) => c.id === groupId);
    if (idx < 0) return;
    const removed = columns[idx];
    if (!canRemoveMeetingMediaGroup(removed)) return;
    const next = columns.filter((c) => c.id !== groupId);
    setColumns(next);
    persist(next);
  };

  const requestRemoveGroup = (col) => {
    if (!canRemoveMeetingMediaGroup(col)) return;
    setDeleteGroupTarget(col);
  };

  const confirmRemoveGroup = () => {
    if (!deleteGroupTarget?.id) return;
    removeGroup(deleteGroupTarget.id);
    setDeleteGroupTarget(null);
  };

  const setGroupLabel = (groupId, label) => {
    const next = columns.map((c) => (c.id === groupId ? { ...c, label } : c));
    setColumns(next);
    persist(next);
  };

  const toggleSelect = (photoId) => {
    const id = Number(photoId);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectGroupPhotos = (groupPhotos, selectAll) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const p of groupPhotos || []) {
        const id = Number(p.id);
        if (selectAll) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  const selectedOrderedPhotos = useMemo(() => {
    const out = [];
    for (const col of columns) {
      for (const p of col.photos || []) {
        if (selectedIds.has(Number(p.id))) out.push(p);
      }
    }
    return out;
  }, [columns, selectedIds]);

  const total = useMemo(
    () => columns.reduce((n, c) => n + (c.photos?.length || 0), 0),
    [columns],
  );

  const uploaderOptions = useMemo(() => {
    const names = new Set();
    let hasNone = false;
    for (const photo of photos || []) {
      const name = String(photo?.uploadedByUsername || "").trim();
      if (name) names.add(name);
      else hasNone = true;
    }
    return {
      names: [...names].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" }),
      ),
      hasNone,
    };
  }, [photos]);

  const viewColumns = useMemo(() => {
    if (!uploaderFilter) return columns;
    return (columns || [])
      .map((col) => ({
        ...col,
        photos: (col.photos || []).filter((photo) => {
          const name = String(photo?.uploadedByUsername || "").trim();
          if (uploaderFilter === "__none__") return !name;
          return name === uploaderFilter;
        }),
      }))
      .filter((col) => (col.photos || []).length > 0);
  }, [columns, uploaderFilter]);

  const viewTotal = useMemo(
    () => viewColumns.reduce((n, c) => n + (c.photos?.length || 0), 0),
    [viewColumns],
  );

  useEffect(() => {
    if (!uploaderFilter) return;
    if (uploaderFilter === "__none__") {
      if (!uploaderOptions.hasNone) setUploaderFilter("");
      return;
    }
    if (!uploaderOptions.names.includes(uploaderFilter)) {
      setUploaderFilter("");
    }
  }, [uploaderFilter, uploaderOptions]);

  const restColumns = useMemo(() => {
    if (galleryMode) {
      // Galería: álbumes primero (visibles), luego bandeja sin agrupar.
      const named = viewColumns.filter((c) => !c.ungrouped);
      const ungrouped = viewColumns.filter((c) => c.ungrouped);
      return [...named, ...ungrouped];
    }
    return viewColumns;
  }, [viewColumns, galleryMode]);

  const expandAllGroups = (on) => {
    if (on) {
      setExpandedGroupIds(new Set(columns.map((c) => String(c.id))));
      return;
    }
    setExpandedGroupIds(new Set());
  };

  const toggleGroupExpanded = (groupId) => {
    const key = String(groupId);
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const createGroupWithPhotos = useCallback(
    (photosToGroup = []) => {
      const toMove = (Array.isArray(photosToGroup) ? photosToGroup : []).filter(
        Boolean,
      );
      const moveIds = new Set(toMove.map((p) => Number(p.id)));
      const newId = newMeetingMediaGroupId();
      const namedCount = (columnsRef.current || []).filter(
        (c) => !c.ungrouped,
      ).length;
      const stripped = (columnsRef.current || []).map((c) => ({
        ...c,
        photos: moveIds.size
          ? (c.photos || []).filter((p) => !moveIds.has(Number(p.id)))
          : [...(c.photos || [])],
      }));
      const next = appendMeetingMediaGroup(stripped, {
        id: newId,
        label: t("Meetings.mediaGroupDefault", { n: namedCount + 1 }),
        photos: toMove.length ? [...toMove] : [],
      });
      columnsRef.current = next;
      setColumns(next);
      persist(next);
      setExpandedGroupIds((prev) => new Set(prev).add(String(newId)));
      if (moveIds.size) setSelectedIds(new Set());
      if (typeof window !== "undefined") {
        window.requestAnimationFrame(() => {
          const el = document.getElementById(`meeting-media-group-${newId}`);
          if (el?.scrollIntoView) {
            el.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }
        });
      }
      return newId;
    },
    [t, persist],
  );

  const addGroup = useCallback(() => {
    createGroupWithPhotos(selectedOrderedPhotos);
  }, [createGroupWithPhotos, selectedOrderedPhotos]);

  const createGroupFromPhotoIds = useCallback(
    (photoIds = []) => {
      const want = new Set(
        (Array.isArray(photoIds) ? photoIds : [])
          .map((id) => Number(id))
          .filter((n) => Number.isFinite(n) && n > 0),
      );
      if (!want.size) return null;
      const byId = new Map();
      for (const col of columnsRef.current) {
        for (const p of col.photos || []) {
          const id = Number(p.id);
          if (want.has(id) && !byId.has(id)) byId.set(id, p);
        }
      }
      const ordered = [...want].map((id) => byId.get(id)).filter(Boolean);
      if (!ordered.length) return null;
      return createGroupWithPhotos(ordered);
    },
    [createGroupWithPhotos],
  );

  const namedGroupOptions = useMemo(
    () =>
      columns
        .filter((c) => c && !c.ungrouped)
        .map((c) => ({
          id: c.id,
          label:
            String(c.label || "").trim() || t("Meetings.mediaGroupUnnamed"),
          count: (c.photos || []).length,
        })),
    [columns, t],
  );

  const movePhotosToGroup = useCallback(
    (photoIds, destGroupId) => {
      const next = moveMeetingBoardPhotos(
        columnsRef.current,
        photoIds,
        destGroupId,
      );
      if (!next) return false;
      setColumns(next);
      persist(next);
      setSelectedIds(new Set());
      setExpandedGroupIds((prev) => new Set(prev).add(String(destGroupId)));
      if (typeof window !== "undefined") {
        window.requestAnimationFrame(() => {
          document
            .getElementById(`meeting-media-group-${destGroupId}`)
            ?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
        });
      }
      return true;
    },
    [persist],
  );

  const moveSelectionToGroup = useCallback(
    (destGroupId) => {
      const ids = selectedOrderedPhotos.map((p) => Number(p.id));
      return movePhotosToGroup(ids, destGroupId);
    },
    [movePhotosToGroup, selectedOrderedPhotos],
  );

  const createSampleFromSelection = useCallback(() => {
    if (!selectedOrderedPhotos.length || !onCreateSample) return;
    onCreateSample(selectedOrderedPhotos);
  }, [onCreateSample, selectedOrderedPhotos]);

  const linkSampleFromSelection = useCallback(() => {
    if (!selectedOrderedPhotos.length || !onLinkSample) return;
    onLinkSample(selectedOrderedPhotos);
  }, [onLinkSample, selectedOrderedPhotos]);

  const unlinkSampleFromSelection = useCallback(() => {
    if (!selectedOrderedPhotos.length || !onUnlinkSample) return;
    onUnlinkSample(selectedOrderedPhotos);
  }, [onUnlinkSample, selectedOrderedPhotos]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  /** Todas las piezas visibles (respeta filtro Quién). Galería y Detalle. */
  const selectAllVisible = useCallback(() => {
    const next = new Set();
    for (const col of viewColumns) {
      for (const p of col.photos || []) {
        const id = Number(p.id);
        if (Number.isFinite(id) && id > 0) next.add(id);
      }
    }
    setSelectedIds(next);
  }, [viewColumns]);

  const allVisibleSelected = useMemo(() => {
    if (!viewTotal) return false;
    let count = 0;
    for (const col of viewColumns) {
      for (const p of col.photos || []) {
        if (selectedIds.has(Number(p.id))) count += 1;
      }
    }
    return count > 0 && count === viewTotal;
  }, [viewColumns, viewTotal, selectedIds]);

  const revealPhoto = useCallback(
    (photoId) => {
      const idNum = Number(photoId);
      if (!Number.isFinite(idNum)) return;
      const col = columns.find((c) =>
        (c.photos || []).some((p) => Number(p.id) === idNum),
      );
      if (!col) return;
      setExpandedGroupIds((prev) => new Set(prev).add(String(col.id)));
    },
    [columns],
  );

  useImperativeHandle(
    ref,
    () => ({
      addGroup,
      createGroupFromPhotoIds,
      movePhotosToGroup,
      getNamedGroups: () => namedGroupOptions,
      createSample: createSampleFromSelection,
      linkSample: linkSampleFromSelection,
      unlinkSample: unlinkSampleFromSelection,
      getSelectedCount: () => selectedOrderedPhotos.length,
      getSelectedPhotoIds: () =>
        selectedOrderedPhotos.map((p) => Number(p.id)).filter((n) => n > 0),
      clearSelection,
      selectAllVisible,
      allVisibleSelected: () => allVisibleSelected,
      revealPhoto,
      getMediaGroups: () => mediaGroupsFromBoard(columns),
      flushSaveGroups,
    }),
    [
      addGroup,
      createGroupFromPhotoIds,
      movePhotosToGroup,
      namedGroupOptions,
      createSampleFromSelection,
      linkSampleFromSelection,
      unlinkSampleFromSelection,
      clearSelection,
      selectAllVisible,
      allVisibleSelected,
      revealPhoto,
      selectedOrderedPhotos,
      columns,
      flushSaveGroups,
    ],
  );

  useEffect(() => {
    const count = selectedOrderedPhotos.length;
    const linkedCount =
      selectedOrderedPhotos.filter(meetingPhotoIsLinked).length;
    onSelectionChange?.({
      count,
      allLinked: count > 0 && linkedCount === count,
      anyLinked: linkedCount > 0,
      linkedCount,
    });
  }, [selectedOrderedPhotos, onSelectionChange]);

  const confirmDeleteMedia = () => {
    if (!deleteTarget?.id || !onDeleteMedia || deletingMedia) return;
    const targetId = deleteTarget.id;
    // Cerrar al momento: el DELETE sigue en segundo plano (optimistic en la página).
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(Number(targetId));
      return next;
    });
    setDeleteTarget(null);
    setDeletingMedia(true);
    Promise.resolve(onDeleteMedia(targetId))
      .catch(() => {
        /* error ya mostrado en la página */
      })
      .finally(() => {
        setDeletingMedia(false);
      });
  };

  const deleteTargetIsVideo =
    deleteTarget &&
    (deleteTarget.kind === "video" ||
      mediaKindFromMime(deleteTarget.mimeType) === "video");

  const setAlbumPage = (groupId, page) => {
    setAlbumPages((prev) => {
      const current = Number(prev[groupId]) || 0;
      if (current === page) return prev;
      return { ...prev, [groupId]: page };
    });
  };

  const clearAlbumLift = (groupId) => {
    const node = albumCardRefs.current.get(String(groupId));
    if (!node) return;
    node.style.transform = "";
    node.style.zIndex = "";
    node.style.boxShadow = "";
  };

  const placeGhost = (node, x, y) => {
    if (!node) return;
    node.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  };

  const startAlbumGroupDrag = (event, col, fromIndex) => {
    if (uploaderFilter) return;
    if (photoGestureRef.current) return;
    if (event.button != null && event.button !== 0) return;
    if (event.target?.closest?.("button, a, .MuiCheckbox-root")) return;
    event.preventDefault();
    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startY = event.clientY;
    const groupId = String(col.id);
    const accent = theme.palette.primary.main;
    const field = event.target?.closest?.("input, textarea") || null;
    clearAlbumLift(groupId);
    let raf = 0;
    const gesture = {
      id: groupId,
      fromIndex,
      pointerId,
      armed: false,
      slots: null,
      painted: null,
      ghost: null,
      stop() {
        if (raf) cancelAnimationFrame(raf);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      },
    };
    const measure = () =>
      [...document.querySelectorAll("[data-album-card]")].map((node) => {
        const rect = node.getBoundingClientRect();
        return {
          id: node.getAttribute("data-album-card"),
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        };
      });
    const paintOver = (id) => {
      if (gesture.painted === id) return;
      if (gesture.painted) {
        const prev = albumCardRefs.current.get(gesture.painted);
        if (prev) prev.style.outline = "";
      }
      gesture.painted = id;
      if (!id) return;
      const node = albumCardRefs.current.get(id);
      if (node) node.style.outline = `2px solid ${accent}`;
    };
    const clearPaint = () => {
      paintOver(null);
      const source = albumCardRefs.current.get(groupId);
      if (source) source.style.opacity = "";
      if (gesture.ghost) {
        gesture.ghost.remove();
        gesture.ghost = null;
      }
    };
    const onMove = (e) => {
      if (e.pointerId !== pointerId || groupGestureRef.current !== gesture)
        return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!gesture.armed) {
        if (Math.abs(dx) + Math.abs(dy) < 4) return;
        gesture.armed = true;
        gesture.slots = measure();
        document.body.style.cursor = "grabbing";
        document.body.style.userSelect = "none";
        field?.blur?.();
        window.getSelection?.()?.removeAllRanges?.();
        const source = albumCardRefs.current.get(groupId);
        if (source) source.style.opacity = "0.45";
        const ghost = document.createElement("div");
        ghost.textContent = `${col.label || ""}  ${(col.photos || []).length}`;
        ghost.style.cssText = [
          "position:fixed",
          "top:0",
          "left:0",
          "z-index:1600",
          "pointer-events:none",
          "padding:8px 12px",
          "border-radius:14px",
          "background:#fff",
          "border:1px solid rgba(15,23,42,0.16)",
          "box-shadow:0 14px 32px rgba(15,23,42,0.18)",
          "font:700 13px/1.2 sans-serif",
          "color:#0f172a",
          "white-space:nowrap",
        ].join(";");
        document.body.appendChild(ghost);
        gesture.ghost = ghost;
      }
      if (gesture.ghost) {
        gesture.ghost.style.transform = `translate3d(${e.clientX + 12}px, ${e.clientY + 12}px, 0)`;
      }
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        // Remedir: al reordenar cambia el wrap y los huecos de abajo.
        gesture.slots = measure();
        const dest = meetingAlbumInsertIndex(
          gesture.slots,
          { x: e.clientX, y: e.clientY },
          fromIndex,
        );
        gesture.dest = dest;
        let overId = null;
        if (typeof dest === "number" && gesture.slots) {
          const slot =
            dest >= gesture.slots.length
              ? gesture.slots[gesture.slots.length - 1]
              : gesture.slots[dest];
          overId = slot && slot.id !== groupId ? slot.id : null;
        }
        paintOver(overId);
      });
    };
    const onUp = (e) => {
      if (e.pointerId !== pointerId) return;
      gesture.stop();
      if (groupGestureRef.current === gesture) groupGestureRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      clearAlbumLift(groupId);
      if (!gesture.armed) {
        clearPaint();
        field?.focus?.();
        return;
      }
      gesture.slots = measure();
      const dest = meetingAlbumInsertIndex(
        gesture.slots,
        { x: e.clientX, y: e.clientY },
        fromIndex,
      );
      clearPaint();
      if (dest == null) return;
      const next = moveMeetingBoardGroup(columnsRef.current, groupId, dest);
      if (!next) return;
      columnsRef.current = next;
      setColumns(next);
      persist(next);
    };
    groupGestureRef.current?.stop?.();
    groupGestureRef.current = gesture;
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };
  const startAlbumPhotoDrag = (event, photo, sourceGroupId) => {
    if (event.button != null && event.button !== 0) return;
    if (groupGestureRef.current) return;
    meetingDragHandled = false;
    const videoDrag =
      photo.kind === "video" || mediaKindFromMime(photo.mimeType) === "video";
    if (event.pointerType === "touch" || videoDrag) event.preventDefault();
    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startY = event.clientY;
    const thumb = event.currentTarget;
    const rect = thumb?.getBoundingClientRect?.();
    const w = rect?.width || 64;
    const h = rect?.height || 64;
    const video =
      photo.kind === "video" || mediaKindFromMime(photo.mimeType) === "video";
    const accent = theme.palette.primary.main;
    const stackGreen = theme.palette.success.main;
    const sourceId = String(sourceGroupId);
    let raf = 0;
    const gesture = {
      pointerId,
      armed: false,
      overId: null,
      overPhotoId: null,
      paintedNode: null,
      ghost: null,
      thumb,
      stop() {
        if (raf) cancelAnimationFrame(raf);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      },
    };
    const paintZone = (id) => {
      if (gesture.paintedId === id) return;
      if (gesture.paintedNode) {
        gesture.paintedNode.style.outline = "";
        gesture.paintedNode.style.boxShadow = "";
        gesture.paintedNode.style.backgroundColor = "";
      }
      gesture.paintedId = id || null;
      if (!id) {
        gesture.paintedNode = null;
        return;
      }
      const node =
        albumCardRefs.current.get(id) || looseDropRefs.current.get(id);
      gesture.paintedNode = node || null;
      if (node) {
        node.style.outline = `2px solid ${accent}`;
        node.style.boxShadow = `0 0 0 3px ${accent}33`;
        node.style.backgroundColor = `${accent}18`;
      }
    };
    const paintStackThumb = (photoId) => {
      const key = photoId != null ? String(photoId) : null;
      if (gesture.paintedStackId === key) return;
      if (gesture.paintedStackNode) {
        gesture.paintedStackNode.style.outline = "";
        gesture.paintedStackNode.style.outlineOffset = "";
        gesture.paintedStackNode.style.transform = "";
        gesture.paintedStackNode.style.boxShadow = "";
        gesture.paintedStackNode.style.zIndex = "";
        gesture.paintedStackNode.style.overflow = "";
      }
      gesture.paintedStackId = key;
      if (!key) {
        gesture.paintedStackNode = null;
        return;
      }
      const node = document.querySelector(`[data-meeting-photo-id="${key}"]`);
      gesture.paintedStackNode = node || null;
      if (node) {
        node.style.outline = `3px solid ${stackGreen}`;
        node.style.outlineOffset = "4px";
        node.style.transform = "scale(1.06)";
        node.style.boxShadow = `0 0 0 6px ${stackGreen}66, 0 10px 24px ${stackGreen}88`;
        node.style.zIndex = "5";
        node.style.overflow = "visible";
      }
    };
    const clearPaint = () => {
      paintZone(null);
      paintStackThumb(null);
      if (thumb) thumb.style.opacity = "";
      if (gesture.ghost) {
        gesture.ghost.remove();
        gesture.ghost = null;
      }
    };
    gesture.cancel = () => {
      gesture.cancelled = true;
      gesture.armed = false;
      clearPaint();
      gesture.stop();
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      if (photoGestureRef.current === gesture) photoGestureRef.current = null;
    };
    const hitTest = (x, y) => {
      const under = document.elementFromPoint(x, y);
      const zone = under?.closest?.("[data-album-card], [data-loose-drop]");
      const overId =
        zone?.getAttribute("data-album-card") ||
        zone?.getAttribute("data-loose-drop") ||
        null;
      gesture.overId = overId && overId !== sourceId ? overId : null;
      const thumbHit = under?.closest?.("[data-meeting-photo-id]");
      const hitId = thumbHit?.getAttribute("data-meeting-photo-id") || null;
      gesture.overPhotoId =
        hitId && String(hitId) !== String(photo.id) ? hitId : null;
      const stackIds = meetingAlbumStackPhotoIds(
        columnsRef.current,
        photo.id,
        gesture.overPhotoId,
      );
      gesture.stackPhotoIds = stackIds;
      if (stackIds) {
        paintZone(null);
        paintStackThumb(gesture.overPhotoId);
      } else {
        paintStackThumb(null);
        paintZone(gesture.overId);
      }
    };
    const onMove = (e) => {
      if (e.pointerId !== pointerId || photoGestureRef.current !== gesture)
        return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!gesture.armed) {
        if (Math.abs(dx) + Math.abs(dy) < 4) return;
        gesture.armed = true;
        thumb?.setAttribute?.("data-dragged", "1");
        if (thumb) thumb.style.opacity = "0.35";
        document.body.style.cursor = "grabbing";
        document.body.style.userSelect = "none";
        const ghost = document.createElement("div");
        ghost.style.cssText = [
          "position:fixed",
          "top:0",
          "left:0",
          "z-index:1600",
          "pointer-events:none",
          `width:${w}px`,
          `height:${h}px`,
          "border-radius:10px",
          "overflow:hidden",
          `background:${video ? "#111827" : "#cbd5e1"}`,
          "box-shadow:0 12px 28px rgba(15,23,42,0.28)",
          "opacity:0.92",
        ].join(";");
        if (video) {
          ghost.textContent = "▶";
          ghost.style.display = "grid";
          ghost.style.placeItems = "center";
          ghost.style.color = "#fff";
          ghost.style.fontSize = "14px";
        }
        document.body.appendChild(ghost);
        gesture.ghost = ghost;
      }
      placeGhost(gesture.ghost, e.clientX - w / 2, e.clientY - h / 2);
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        hitTest(e.clientX, e.clientY);
      });
    };
    const onUp = (e) => {
      if (e.pointerId !== pointerId) return;
      gesture.stop();
      if (photoGestureRef.current === gesture) photoGestureRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      thumb?.removeAttribute?.("data-dragged");
      if (gesture.cancelled || !gesture.armed || meetingDragHandled) {
        clearPaint();
        return;
      }
      hitTest(e.clientX, e.clientY);
      const overId = gesture.overId;
      const overPhotoId = gesture.overPhotoId;
      const stackPhotoIds = gesture.stackPhotoIds;
      clearPaint();
      if (stackPhotoIds?.length) {
        createGroupFromPhotoIds(stackPhotoIds);
        meetingDragHandled = true;
        return;
      }
      const destColumnId = overId || sourceId;
      const sameGroup = String(destColumnId) === sourceId;
      const col = (columnsRef.current || []).find(
        (c) => String(c.id) === destColumnId,
      );
      let destIndex = null;
      // Solo reordenar dentro del mismo álbum; al meter en otro → al final
      // (evita el «mete/saca» al soltar encima de una miniatura).
      if (sameGroup && overPhotoId) {
        const index = (col?.photos || []).findIndex(
          (p) => String(p.id) === String(overPhotoId),
        );
        if (index >= 0) destIndex = index;
      }
      if (!overId && destIndex == null) return;
      const next = moveMeetingBoardPhoto(
        columnsRef.current,
        photo.id,
        destColumnId,
        destIndex,
      );
      if (!next) return;
      meetingDragHandled = true;
      columnsRef.current = next;
      setColumns(next);
      persist(next);
    };
    photoGestureRef.current?.stop?.();
    photoGestureRef.current = gesture;
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const prefetchMeetingPhotoFile = useCallback((photo) => {
    if (!photo) return;
    const video =
      photo.kind === "video" || mediaKindFromMime(photo.mimeType) === "video";
    if (video) return;
    lastHoveredPhotoRef.current = photo;
    if (cachedMeetingPhotoFile(photo)) return;
    fetchMeetingPhotoAsFile(photo).catch(() => {});
  }, []);

  const openMediaFromThumb = useCallback(
    (photoId) => {
      // Si no cancelamos, el pointer-gesture deja opacity/listeners y el drag “muere”.
      photoGestureRef.current?.cancel?.();
      openMedia?.(photoId);
    },
    [openMedia],
  );

  const beginOsPhotoDrag = useCallback(
    (event, photo, sourceGroupId, options = {}) => {
      // Evita que el gesto pointer suelte después y cree un grupo fantasma
      photoGestureRef.current?.cancel?.();
      lastHoveredPhotoRef.current = photo;
      const fromCopyIcon = Boolean(options.fromCopyIcon);
      meetingPhotoCopyIconDrag = fromCopyIcon;
      const payload = {
        id: Number(photo?.id),
        sourceGroupId: String(sourceGroupId || ""),
      };
      activeMeetingPhotoDrag = payload;
      // Desde icono Copiar: solo export SO/portapapeles, no rearrange interno.
      meetingDragHandled = fromCopyIcon;
      const safari = isSafariBrowser();
      const thumbNode =
        event.currentTarget?.closest?.("[data-meeting-photo-id]") ||
        event.currentTarget;
      const file =
        cachedMeetingPhotoFile(photo) ||
        fileFromLoadedImage(
          thumbNode?.querySelector?.("img"),
          meetingPhotoDownloadName(photo),
        );
      // Safari: File sí; DownloadURL es solo Chromium. Mac→AI a menudo acepta File.
      const ok = startMeetingPhotoDrag(event, photo, file, {
        cancelIfEmpty: false,
        downloadUrl: !safari,
      });
      if (!fromCopyIcon) {
        try {
          event.dataTransfer.setData(
            MEETING_PHOTO_DRAG_TYPE,
            JSON.stringify(payload),
          );
        } catch {
          /* ignore */
        }
      }
      if (file) {
        copyMeetingPhotoFileToClipboard(file)
          .then((copied) => {
            if (copied) meetingPhotoClipboardPrimed = true;
          })
          .catch(() => {});
      } else {
        fetchMeetingPhotoAsFile(photo)
          .then((fetched) => {
            if (!fetched) return;
            return copyMeetingPhotoFileToClipboard(fetched).then((copied) => {
              if (copied) meetingPhotoClipboardPrimed = true;
            });
          })
          .catch(() => {});
      }
      if (!ok || !file) {
        fetchMeetingPhotoAsFile(photo).catch(() => {});
      }
    },
    [],
  );

  const beginCopyIconDrag = useCallback(
    (event, photo, sourceGroupId) => {
      beginOsPhotoDrag(event, photo, sourceGroupId, { fromCopyIcon: true });
    },
    [beginOsPhotoDrag],
  );

  const applyNativePhotoDrop = useCallback(
    (event, destColumnId) => {
      let data = null;
      try {
        const raw = event.dataTransfer.getData(MEETING_PHOTO_DRAG_TYPE) || "";
        if (raw) data = JSON.parse(raw);
      } catch {
        data = null;
      }
      if (!data) data = activeMeetingPhotoDrag;
      const photoId = Number(data?.id);
      // Sin id de foto de la reunión → es drop del SO / galería.
      if (!Number.isFinite(photoId) || photoId <= 0) return false;
      event.preventDefault();
      event.stopPropagation();
      clearNativeDropUi();
      if (meetingDragHandled) return true;
      const overPhotoId = event.target
        ?.closest?.("[data-meeting-photo-id]")
        ?.getAttribute?.("data-meeting-photo-id");
      // Solo apilar si AMBAS piezas ya están en sin agrupar (no al sacar de un álbum).
      const stackIds = meetingAlbumStackPhotoIds(
        columnsRef.current,
        photoId,
        overPhotoId,
      );
      if (stackIds?.length) {
        createGroupFromPhotoIds(stackIds);
        meetingDragHandled = true;
        clearActiveMeetingPhotoDrag();
        return true;
      }
      const sourceId = String(data?.sourceGroupId || "");
      const destId = String(destColumnId || "");
      if (!destId) return false;
      const sameGroup = destId === sourceId;
      let destIndex = null;
      if (sameGroup && overPhotoId && String(overPhotoId) !== String(photoId)) {
        const col = (columnsRef.current || []).find(
          (c) => String(c.id) === destId,
        );
        const index = (col?.photos || []).findIndex(
          (p) => String(p.id) === String(overPhotoId),
        );
        if (index >= 0) destIndex = index;
      }
      if (sameGroup && destIndex == null) {
        clearActiveMeetingPhotoDrag();
        return true;
      }
      const next = moveMeetingBoardPhoto(
        columnsRef.current,
        photoId,
        destId,
        destIndex,
      );
      if (!next) {
        clearActiveMeetingPhotoDrag();
        return true;
      }
      meetingDragHandled = true;
      columnsRef.current = next;
      setColumns(next);
      persist(next);
      clearActiveMeetingPhotoDrag();
      return true;
    },
    [clearNativeDropUi, createGroupFromPhotoIds, persist],
  );

  const handleNativeDragOverZone = useCallback(
    (event, zoneId) => {
      if (!isMeetingPhotoDragEvent(event)) return false;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
      const sourceId = Number(activeMeetingPhotoDrag?.id);
      const overPhotoId = event.target
        ?.closest?.("[data-meeting-photo-id]")
        ?.getAttribute?.("data-meeting-photo-id");
      const stackIds = meetingAlbumStackPhotoIds(
        columnsRef.current,
        sourceId,
        overPhotoId,
      );
      if (stackIds?.length) {
        setNativeDropZone(null, overPhotoId);
      } else {
        setNativeDropZone(zoneId, null);
      }
      return true;
    },
    [setNativeDropZone],
  );

  const renderAlbumCard = (col, index) => {
    const albumPhotos = col.photos || [];
    const pageCount = Math.max(1, Math.ceil(albumPhotos.length / 9));
    const page = Math.min(Number(albumPages[col.id]) || 0, pageCount - 1);
    const visiblePhotos = albumPhotos.slice(page * 9, page * 9 + 9);
    const groupIds = albumPhotos.map((p) => Number(p.id));
    const allSelected =
      groupIds.length > 0 && groupIds.every((id) => selectedIds.has(id));
    const someSelected =
      !allSelected && groupIds.some((id) => selectedIds.has(id));
    const groupId = String(col.id);
    return (
      <Box
        key={groupId}
        id={`meeting-media-group-${col.id}`}
        data-album-card={groupId}
        onDragOver={(e) => {
          handleNativeDragOverZone(e, groupId);
        }}
        onDragLeave={(e) => {
          if (!isMeetingPhotoDragEvent(e)) return;
          const related = e.relatedTarget;
          if (related && e.currentTarget.contains(related)) return;
          clearNativeDropUi();
        }}
        onDrop={(e) => {
          applyNativePhotoDrop(e, groupId);
        }}
        ref={(node) => {
          if (node) albumCardRefs.current.set(groupId, node);
          else albumCardRefs.current.delete(groupId);
        }}
        sx={{
          width: "100%",
          minWidth: 0,
          position: "relative",
          outline:
            nativeDropUi?.zoneId === groupId && !nativeDropUi?.stackPhotoId
              ? `2px solid ${theme.palette.primary.main}`
              : "none",
          boxShadow:
            nativeDropUi?.zoneId === groupId && !nativeDropUi?.stackPhotoId
              ? `0 0 0 3px ${theme.palette.primary.main}33`
              : "none",
          borderRadius: MEETING_CARD_RADIUS,
        }}
      >
        <Box
          sx={{
            p: 1,
            borderRadius: MEETING_CARD_RADIUS,
            border: "1px solid",
            ...meetingAlbumGroupSurfaceSx(theme),
            borderColor: allSelected
              ? theme.palette.primary.main
              : meetingAlbumGroupSurfaceSx(theme).borderColor,
            bgcolor:
              nativeDropUi?.zoneId === groupId && !nativeDropUi?.stackPhotoId
                ? alpha(theme.palette.primary.main, 0.16)
                : meetingAlbumGroupSurfaceSx(theme).bgcolor,
            height: "100%",
            boxShadow: "none",
          }}
        >
          <Box
            onPointerDown={(e) => {
              e.currentTarget.dataset.sx = String(e.clientX);
              e.currentTarget.dataset.sy = String(e.clientY);
            }}
            onPointerUp={(e) => {
              if (e.target.closest("[data-meeting-photo-id]")) return;
              const x = Number(e.currentTarget.dataset.sx);
              const y = Number(e.currentTarget.dataset.sy);
              if (!Number.isFinite(x)) return;
              const dx = e.clientX - x;
              const dy = e.clientY - y;
              if (Math.abs(dx) < 36 || Math.abs(dx) < Math.abs(dy)) return;
              setAlbumPage(
                col.id,
                Math.min(pageCount - 1, Math.max(0, page + (dx < 0 ? 1 : -1))),
              );
            }}
            sx={{
              display: "grid",
              position: "relative",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: nativeDropUi?.stackPhotoId ? 1.25 : 0.75,
              p: nativeDropUi?.stackPhotoId ? 1 : 0.5,
              aspectRatio: "1 / 1",
              overflow: nativeDropUi?.stackPhotoId ? "visible" : "hidden",
              alignContent: "start",
              borderRadius: 2,
              bgcolor: "transparent",
            }}
          >
            {visiblePhotos.map((photo) => (
              <AlbumThumbMemo
                key={String(photo.id)}
                photo={photo}
                selected={selectedIds.has(Number(photo.id))}
                stackHighlight={
                  String(nativeDropUi?.stackPhotoId || "") === String(photo.id)
                }
                onToggleSelect={toggleSelect}
                openMedia={openMediaFromThumb}
                onCopyPhoto={copyPhotoForIllustrator}
                onCopyIconDragStart={(dragEvent) =>
                  beginCopyIconDrag(dragEvent, photo, groupId)
                }
                onStartDrag={(dragEvent) =>
                  startAlbumPhotoDrag(dragEvent, photo, groupId)
                }
                onOsDragStart={(dragEvent) =>
                  beginOsPhotoDrag(dragEvent, photo, groupId)
                }
                onPrefetchPhoto={prefetchMeetingPhotoFile}
                onNativeDragOverPhoto={(pid) => {
                  const stackIds = meetingAlbumStackPhotoIds(
                    columnsRef.current,
                    activeMeetingPhotoDrag?.id,
                    pid,
                  );
                  if (stackIds?.length) setNativeDropZone(null, pid);
                  else setNativeDropZone(groupId, null);
                }}
                onNativeDragLeavePhoto={clearNativeDropUi}
                onRequestDelete={
                  onDeleteMedia ? (p) => setDeleteTarget(p) : undefined
                }
                t={t}
                theme={theme}
              />
            ))}
            {pageCount > 1 ? (
              <Stack
                direction="row"
                justifyContent="center"
                spacing={0.5}
                sx={{
                  position: "absolute",
                  left: "50%",
                  bottom: 6,
                  transform: "translateX(-50%)",
                  zIndex: 2,
                  px: 0.75,
                  py: 0.3,
                  borderRadius: 999,
                  bgcolor: alpha(theme.palette.background.paper, 0.92),
                }}
              >
                {Array.from({ length: pageCount }, (_, i) => (
                  <Box
                    key={`page-${col.id}-${i}`}
                    component="button"
                    type="button"
                    aria-label={t("Meetings.mediaAlbumPage", {
                      page: i + 1,
                      total: pageCount,
                    })}
                    onClick={() => setAlbumPage(col.id, i)}
                    onMouseDown={stopDragEvent}
                    onPointerDown={stopDragEvent}
                    sx={{
                      width: i === page ? 7 : 5,
                      height: i === page ? 7 : 5,
                      p: 0,
                      border: 0,
                      borderRadius: "50%",
                      cursor: "pointer",
                      bgcolor:
                        i === page
                          ? theme.palette.text.primary
                          : alpha(theme.palette.text.primary, 0.28),
                    }}
                  />
                ))}
              </Stack>
            ) : null}
          </Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.25}
            onPointerDown={(event) => startAlbumGroupDrag(event, col, index)}
            sx={{
              mt: 0.75,
              minWidth: 0,
              minHeight: 32,
              px: 0.25,
              borderRadius: 1.5,
              cursor: uploaderFilter ? "default" : "grab",
              touchAction: uploaderFilter ? "auto" : "none",
              userSelect: "none",
              "&:active": {
                cursor: uploaderFilter ? "default" : "grabbing",
              },
            }}
          >
            <Tooltip title={t("Meetings.selectGroup")}>
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={() => selectGroupPhotos(albumPhotos, !allSelected)}
                onMouseDown={stopDragEvent}
                onTouchStart={stopDragEvent}
                onClick={stopDragEvent}
                inputProps={{ "aria-label": t("Meetings.selectGroup") }}
                sx={{ p: 0.25, flexShrink: 0 }}
              />
            </Tooltip>
            <TextField
              variant="standard"
              value={col.label}
              onChange={(e) => setGroupLabel(col.id, e.target.value)}
              placeholder={t("Meetings.mediaGroupLabelPlaceholder")}
              InputProps={{ disableUnderline: true }}
              inputProps={{
                "aria-label": t("Meetings.mediaGroupLabelPlaceholder"),
                style: {
                  fontSize: 13,
                  fontWeight: 600,
                  padding: 0,
                  lineHeight: 1.2,
                  cursor: uploaderFilter ? "text" : "grab",
                },
              }}
              sx={{ flex: 1, minWidth: 0 }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 700, flexShrink: 0 }}
            >
              {albumPhotos.length}
            </Typography>
            <Tooltip
              title={
                canRemoveMeetingMediaGroup(col)
                  ? t("Meetings.mediaGroupRemove")
                  : t("Meetings.mediaGroupRemoveBlocked")
              }
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => requestRemoveGroup(col)}
                  onMouseDown={stopDragEvent}
                  onTouchStart={stopDragEvent}
                  disabled={!canRemoveMeetingMediaGroup(col)}
                  aria-label={t("Meetings.mediaGroupRemove")}
                  sx={{ p: 0.25 }}
                >
                  <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>
      </Box>
    );
  };

  const startLooseDockResize = (event) => {
    if (event.button != null && event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const pointerId = event.pointerId;
    const startY = event.clientY;
    const startH = looseDockPxRef.current;
    const onMove = (e) => {
      if (e.pointerId !== pointerId) return;
      const maxH = Math.max(
        240,
        Math.round((window.innerHeight || 800) * 0.72),
      );
      const next = Math.min(maxH, Math.max(36, startH - (e.clientY - startY)));
      setLooseDockPx(next);
    };
    const onUp = (e) => {
      if (e.pointerId !== pointerId) return;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      try {
        localStorage.setItem(
          "meetingLooseDockPx",
          String(looseDockPxRef.current),
        );
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const clearFileDropActive = () => {
    fileDragDepthRef.current = 0;
    setFileDropActive(false);
  };

  const handleLooseFileDragEnter = (event) => {
    if (!onDropFiles || !isOsFileDragEvent(event)) return;
    event.preventDefault();
    event.stopPropagation();
    fileDragDepthRef.current += 1;
    setFileDropActive(true);
  };

  const handleLooseFileDragLeave = (event) => {
    if (!onDropFiles || !isOsFileDragEvent(event)) return;
    event.preventDefault();
    event.stopPropagation();
    fileDragDepthRef.current = Math.max(0, fileDragDepthRef.current - 1);
    if (fileDragDepthRef.current === 0) setFileDropActive(false);
  };

  const handleLooseFileDragOver = (event) => {
    if (!onDropFiles || !isOsFileDragEvent(event)) return;
    event.preventDefault();
    event.stopPropagation();
    try {
      event.dataTransfer.dropEffect = "copy";
    } catch {
      /* ignore */
    }
  };

  const handleLooseFileDrop = (event, looseId) => {
    clearFileDropActive();
    const files = Array.from(event.dataTransfer?.files || []).filter(
      isMeetingDropMediaFile,
    );
    let hasMeetingType = false;
    try {
      hasMeetingType = Boolean(
        event.dataTransfer.getData(MEETING_PHOTO_DRAG_TYPE),
      );
    } catch {
      hasMeetingType = dragTypes(event).includes(MEETING_PHOTO_DRAG_TYPE);
    }
    // Galería / escritorio: archivos sin tipo de reunión → subir + dialog nota.
    if (files.length && !hasMeetingType && !activeMeetingPhotoDrag) {
      event.preventDefault();
      event.stopPropagation();
      if (onDropFiles) onDropFiles(files);
      return;
    }
    // Foto de la propia reunión (reordenar / agrupar).
    if (applyNativePhotoDrop(event, looseId)) return;
    if (!onDropFiles || !files.length) return;
    event.preventDefault();
    event.stopPropagation();
    clearActiveMeetingPhotoDrag();
    onDropFiles(files);
  };

  const renderLooseGallery = (col) => {
    const looseId = String(col.id);
    /** Tamaño fijo: la barra no agranda las fotos, solo enseña más filas. */
    const thumbPx = 104;
    const collapsed = looseDockPx <= 44;
    const oneRow = looseDockPx < thumbPx + 72;
    /** Móvil/iPad: flotante siempre visible. Desktop lg+: anclado al panel. */
    const fixedDock = compactChrome;
    const accent = theme.palette.primary.main;
    const dock = (
      <Box
        id={`meeting-media-group-${col.id}`}
        data-loose-drop={looseId}
        ref={(node) => {
          if (node) looseDropRefs.current.set(looseId, node);
          else looseDropRefs.current.delete(looseId);
        }}
        onDragEnter={handleLooseFileDragEnter}
        onDragLeave={handleLooseFileDragLeave}
        onDragOver={(event) => {
          if (handleNativeDragOverZone(event, looseId)) return;
          handleLooseFileDragOver(event);
        }}
        onDragLeave={(event) => {
          if (isMeetingPhotoDragEvent(event)) {
            const related = event.relatedTarget;
            if (related && event.currentTarget.contains(related)) return;
            clearNativeDropUi();
            return;
          }
          handleLooseFileDragLeave(event);
        }}
        onDrop={(event) => handleLooseFileDrop(event, looseId)}
        sx={{
          ...(fixedDock
            ? {
                position: "fixed",
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1200,
              }
            : {
                position: "relative",
                flexShrink: 0,
              }),
          height: looseDockPx,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          pt: 0,
          pb: fixedDock ? "max(6px, env(safe-area-inset-bottom))" : 0.75,
          px: 1.25,
          borderTop: "1px solid",
          borderTopLeftRadius: MEETING_CARD_RADIUS,
          borderTopRightRadius: MEETING_CARD_RADIUS,
          borderColor:
            fileDropActive ||
            (nativeDropUi?.zoneId === looseId && !nativeDropUi?.stackPhotoId)
              ? accent
              : meetingAlbumUngroupedSurfaceSx(theme).borderColor,
          outline:
            fileDropActive ||
            (nativeDropUi?.zoneId === looseId && !nativeDropUi?.stackPhotoId)
              ? `2px solid ${accent}`
              : "none",
          outlineOffset: -2,
          boxShadow:
            fileDropActive ||
            (nativeDropUi?.zoneId === looseId && !nativeDropUi?.stackPhotoId)
              ? `0 0 0 3px ${accent}33`
              : fixedDock
                ? theme.palette.mode === "dark"
                  ? "0 -6px 20px rgba(0,0,0,0.45)"
                  : "0 -6px 18px rgba(15,23,42,0.12)"
                : "none",
          // Fondo sólido: el dock fixed no puede ser alpha o se ve el Summary/álbum detrás.
          bgcolor: meetingAlbumUngroupedSurfaceSx(theme).bgcolor,
          background:
            fileDropActive ||
            (nativeDropUi?.zoneId === looseId && !nativeDropUi?.stackPhotoId)
              ? `linear-gradient(0deg, ${alpha(accent, theme.palette.mode === "dark" ? 0.35 : 0.22)}, ${alpha(accent, theme.palette.mode === "dark" ? 0.35 : 0.22)}), ${meetingAlbumUngroupedSurfaceSx(theme).bgcolor}`
              : meetingAlbumUngroupedSurfaceSx(theme).background,
          backdropFilter: "none",
          opacity: 1,
          isolation: "isolate",
          transition: "background-color 120ms ease, border-color 120ms ease",
        }}
      >
        <Box
          role="separator"
          aria-orientation="horizontal"
          aria-label={t("Meetings.mediaUngrouped")}
          onPointerDown={startLooseDockResize}
          onDoubleClick={() => {
            const next = collapsed ? 132 : 36;
            setLooseDockPx(next);
            try {
              localStorage.setItem("meetingLooseDockPx", String(next));
            } catch {
              /* ignore */
            }
          }}
          sx={{
            flexShrink: 0,
            height: 10,
            mx: -1.25,
            mb: 0.25,
            cursor: "ns-resize",
            touchAction: "none",
            display: "grid",
            placeItems: "center",
            "&:hover > span, &:active > span": {
              bgcolor: alpha(theme.palette.text.primary, 0.45),
              width: 56,
            },
          }}
        >
          <Box
            component="span"
            sx={{
              width: 36,
              height: 4,
              borderRadius: 999,
              bgcolor: alpha(theme.palette.text.primary, 0.28),
            }}
          />
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          onClick={() => {
            const next = collapsed ? 132 : 36;
            setLooseDockPx(next);
            try {
              localStorage.setItem("meetingLooseDockPx", String(next));
            } catch {
              /* ignore */
            }
          }}
          sx={{
            fontWeight: 800,
            display: "block",
            mb: collapsed ? 0 : 0.5,
            cursor: "pointer",
            userSelect: "none",
            flexShrink: 0,
          }}
        >
          {fileDropActive ||
          (nativeDropUi?.zoneId === looseId && !nativeDropUi?.stackPhotoId)
            ? t("Meetings.mediaUngroupedDropActive")
            : t("Meetings.mediaUngrouped")}
          {fileDropActive ||
          (nativeDropUi?.zoneId === looseId && !nativeDropUi?.stackPhotoId)
            ? ""
            : ` · ${col.photos.length}`}
        </Typography>
        <Box
          sx={{
            display: collapsed ? "none" : oneRow ? "flex" : "grid",
            flex: 1,
            minHeight: 0,
            flexDirection: "row",
            flexWrap: "nowrap",
            alignItems: "start",
            alignContent: "start",
            gridTemplateColumns: `repeat(auto-fill, ${thumbPx}px)`,
            gridAutoRows: `${thumbPx}px`,
            gap: nativeDropUi?.stackPhotoId ? 1.5 : 0.85,
            overflowX: oneRow ? "auto" : "hidden",
            overflowY: oneRow ? "hidden" : "auto",
            WebkitOverflowScrolling: "touch",
            scrollSnapType: oneRow ? "x proximity" : "none",
            p: nativeDropUi?.stackPhotoId ? 1 : col.photos.length ? 0.25 : 1,
            borderRadius: 1.5,
            outline:
              fileDropActive || !col.photos.length
                ? `1px dashed ${fileDropActive ? accent : theme.palette.divider}`
                : "none",
            "&::-webkit-scrollbar": { height: 6, width: 6 },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 999,
              bgcolor: alpha(theme.palette.text.primary, 0.22),
            },
            "& > .meeting-album-thumb": {
              flex: "0 0 auto",
              width: thumbPx,
              height: thumbPx,
              scrollSnapAlign: "start",
            },
          }}
        >
          {(col.photos || []).map((photo) => (
            <AlbumThumbMemo
              key={String(photo.id)}
              photo={photo}
              selected={selectedIds.has(Number(photo.id))}
              stackHighlight={
                String(nativeDropUi?.stackPhotoId || "") === String(photo.id)
              }
              onToggleSelect={toggleSelect}
              openMedia={openMediaFromThumb}
              onCopyPhoto={copyPhotoForIllustrator}
              onCopyIconDragStart={(dragEvent) =>
                beginCopyIconDrag(dragEvent, photo, looseId)
              }
              onStartDrag={(dragEvent) =>
                startAlbumPhotoDrag(dragEvent, photo, looseId)
              }
              onOsDragStart={(dragEvent) =>
                beginOsPhotoDrag(dragEvent, photo, looseId)
              }
              onPrefetchPhoto={prefetchMeetingPhotoFile}
              onNativeDragOverPhoto={(pid) => {
                const stackIds = meetingAlbumStackPhotoIds(
                  columnsRef.current,
                  activeMeetingPhotoDrag?.id,
                  pid,
                );
                if (stackIds?.length) setNativeDropZone(null, pid);
                else setNativeDropZone(looseId, null);
              }}
              onNativeDragLeavePhoto={clearNativeDropUi}
              onRequestDelete={
                onDeleteMedia ? (p) => setDeleteTarget(p) : undefined
              }
              t={t}
              theme={theme}
            />
          ))}
          {!col.photos.length ? (
            <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
              {t("Meetings.mediaUngroupedDropHint")}
            </Typography>
          ) : null}
        </Box>
      </Box>
    );
    if (fixedDock && typeof document !== "undefined") {
      return createPortal(dock, document.body);
    }
    return dock;
  };

  const renderGroupColumn = (col) => {
    const collapsed = !expandedGroupIds.has(String(col.id));
    const groupIds = (col.photos || []).map((p) => Number(p.id));
    const allSelected =
      groupIds.length > 0 && groupIds.every((id) => selectedIds.has(id));
    const someSelected =
      !allSelected && groupIds.some((id) => selectedIds.has(id));
    const previewPhotos = col.photos || [];

    return (
      <Box
        key={col.id}
        id={`meeting-media-group-${col.id}`}
        onDragOver={(e) => {
          if (!isMeetingPhotoDragEvent(e)) return;
          e.preventDefault();
          if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
        }}
        onDrop={(e) => {
          applyNativePhotoDrop(e, col.id);
        }}
        sx={{
          border: "1px solid",
          borderRadius: MEETING_CARD_RADIUS,
          p: 1,
          ...(col.ungrouped
            ? meetingAlbumUngroupedSurfaceSx(theme)
            : meetingAlbumGroupSurfaceSx(theme)),
          borderLeft: col.ungrouped
            ? `1px solid ${meetingAlbumUngroupedSurfaceSx(theme).borderColor}`
            : `3px solid ${alpha(theme.palette.text.primary, 0.28)}`,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mb: collapsed ? 0 : 1 }}
        >
          <IconButton
            size="small"
            onClick={() => toggleGroupExpanded(col.id)}
            aria-label={
              collapsed
                ? t("Meetings.mediaGroupsTreeExpand")
                : t("Meetings.mediaGroupsTreeCollapse")
            }
            sx={{ p: 0.25 }}
          >
            {collapsed ? (
              <ChevronRightOutlinedIcon fontSize="small" />
            ) : (
              <ExpandMoreOutlinedIcon fontSize="small" />
            )}
          </IconButton>
          {col.photos.length ? (
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              onChange={() => selectGroupPhotos(col.photos, !allSelected)}
              inputProps={{
                "aria-label": t("Meetings.selectGroup"),
              }}
              sx={{ p: 0.5 }}
            />
          ) : null}
          {col.ungrouped ? (
            <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>
              {t("Meetings.mediaUngrouped")}
              {` · ${col.photos.length}`}
            </Typography>
          ) : (
            <>
              <TextField
                size="small"
                value={col.label}
                onChange={(e) => setGroupLabel(col.id, e.target.value)}
                placeholder={t("Meetings.mediaGroupLabelPlaceholder")}
                sx={{ flex: 1 }}
              />
              <Chip
                size="small"
                label={col.photos.length}
                sx={{
                  height: 20,
                  fontWeight: 700,
                  "& .MuiChip-label": { px: 0.7 },
                }}
              />
              <Tooltip
                title={
                  canRemoveMeetingMediaGroup(col)
                    ? t("Meetings.mediaGroupRemove")
                    : t("Meetings.mediaGroupRemoveBlocked")
                }
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={() => requestRemoveGroup(col)}
                    disabled={!canRemoveMeetingMediaGroup(col)}
                    aria-label={t("Meetings.mediaGroupRemove")}
                  >
                    <DeleteOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Stack>
        {collapsed ? (
          <Droppable droppableId={col.id} isDropDisabled={false}>
            {(provided, snapshot) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                onClick={() => {
                  if ((col.photos || []).length) toggleGroupExpanded(col.id);
                }}
                sx={{
                  minHeight: previewPhotos.length ? 56 : 8,
                  borderRadius: 1,
                  outline: snapshot.isDraggingOver
                    ? `2px dashed ${theme.palette.primary.main}`
                    : previewPhotos.length
                      ? `1px dashed ${theme.palette.divider}`
                      : "none",
                  p: previewPhotos.length ? 0.75 : 0,
                  cursor: previewPhotos.length ? "pointer" : "default",
                  bgcolor: snapshot.isDraggingOver
                    ? "action.selected"
                    : "transparent",
                }}
              >
                {previewPhotos.length ? (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ alignItems: "center" }}
                  >
                    {previewPhotos.map((photo) => {
                      const video =
                        photo.kind === "video" ||
                        mediaKindFromMime(photo.mimeType) === "video";
                      const thumbSrc = meetingPhotoThumbUrl(photo);
                      return (
                        <Box
                          key={photo.id}
                          sx={{
                            position: "relative",
                            width: 44,
                            height: 44,
                            borderRadius: 1,
                            overflow: "hidden",
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: video ? "grey.900" : "action.hover",
                            flexShrink: 0,
                          }}
                        >
                          {thumbSrc ? (
                            <Box
                              component="img"
                              src={thumbSrc}
                              alt=""
                              loading="lazy"
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          ) : null}
                          {video ? (
                            <Box
                              sx={{
                                position: "absolute",
                                inset: 0,
                                display: "grid",
                                placeItems: "center",
                                color: "common.white",
                                fontSize: 10,
                                fontWeight: 700,
                                bgcolor: thumbSrc
                                  ? "transparent"
                                  : "rgba(0,0,0,0.35)",
                              }}
                            >
                              ▶
                            </Box>
                          ) : null}
                        </Box>
                      );
                    })}
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{ fontWeight: 700, ml: 0.5 }}
                    >
                      {t("Meetings.mediaGalleryExpandGroup")}
                    </Typography>
                  </Stack>
                ) : null}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        ) : (
          <Droppable
            droppableId={col.id}
            direction="vertical"
            isDropDisabled={false}
            renderClone={(provided, snapshot, rubric) => {
              const photo = (col.photos || [])[rubric.source.index];
              if (!photo) return <div ref={provided.innerRef} />;
              return (
                <MediaCard
                  photo={photo}
                  provided={provided}
                  isDragging={snapshot.isDragging}
                  notesById={notesById}
                  audioByPhotoId={audioByPhotoId}
                  summaryLang={summaryLang}
                  highlightPhotoId={highlightPhotoId}
                  expandedNoteIds={expandedNoteIds}
                  setExpandedNoteIds={setExpandedNoteIds}
                  openMedia={openMedia}
                  summary={summary}
                  onSaveTeamNote={onSaveTeamNote}
                  onRequestDelete={undefined}
                  selected={selectedIds.has(Number(photo.id))}
                  onToggleSelect={undefined}
                  onOpenLinkedSample={onOpenLinkedSample}
                  compact={false}
                  t={t}
                  theme={theme}
                />
              );
            }}
          >
            {(provided, snapshot) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  ...meetingMediaGroupsFeedSx,
                  minHeight: col.photos.length ? 48 : 88,
                  p: col.photos.length ? 0 : 1,
                  bgcolor: snapshot.isDraggingOver
                    ? "action.selected"
                    : col.ungrouped
                      ? "transparent"
                      : "background.paper",
                  outline: snapshot.isDraggingOver
                    ? `2px dashed ${theme.palette.primary.main}`
                    : col.photos.length
                      ? "none"
                      : `1px dashed ${theme.palette.divider}`,
                  outlineOffset: 2,
                  borderRadius: 1,
                }}
              >
                {col.photos.map((photo, index) => (
                  <Draggable
                    key={String(photo.id)}
                    draggableId={`media-${photo.id}`}
                    index={index}
                  >
                    {(dragProvided, dragSnapshot) => (
                      <MediaCard
                        photo={photo}
                        provided={dragProvided}
                        isDragging={dragSnapshot.isDragging}
                        notesById={notesById}
                        audioByPhotoId={audioByPhotoId}
                        summaryLang={summaryLang}
                        highlightPhotoId={highlightPhotoId}
                        expandedNoteIds={expandedNoteIds}
                        setExpandedNoteIds={setExpandedNoteIds}
                        openMedia={openMedia}
                        summary={summary}
                        onSaveTeamNote={onSaveTeamNote}
                        onRequestDelete={
                          onDeleteMedia ? (p) => setDeleteTarget(p) : undefined
                        }
                        selected={selectedIds.has(Number(photo.id))}
                        onToggleSelect={toggleSelect}
                        onOpenLinkedSample={onOpenLinkedSample}
                        compact={false}
                        t={t}
                        theme={theme}
                      />
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
                {!col.photos.length ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ p: 1 }}
                  >
                    {t("Meetings.mediaGroupEmpty")}
                  </Typography>
                ) : null}
              </Box>
            )}
          </Droppable>
        )}
      </Box>
    );
  };

  const mediaCountLabel =
    uploaderFilter && viewTotal !== total
      ? `${viewTotal}/${total}`
      : String(uploaderFilter ? viewTotal : total);
  const headerChipSx = {
    height: 22,
    fontWeight: 800,
    fontSize: "0.72rem",
    "& .MuiChip-label": { px: 0.85 },
  };
  const uploaderChipSx = {
    height: 28,
    minHeight: 28,
    maxHeight: 28,
    fontWeight: 800,
    fontSize: "0.72rem",
    borderRadius: "8px",
    "& .MuiChip-label": { px: 0.9, py: 0 },
  };
  const mediaToolbar = (
    <>
      <Tooltip
        title={
          savingGroups
            ? t("Meetings.mediaGroupsSaving")
            : `${t("Meetings.mediaGallery")} · ${mediaCountLabel}`
        }
      >
        <Chip
          size="small"
          variant="outlined"
          label={
            compactChrome ? (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.35,
                }}
              >
                <PhotoLibraryOutlinedIcon sx={{ fontSize: 15 }} />
                <VideocamOutlinedIcon sx={{ fontSize: 15 }} />
                <Box component="span" sx={{ fontWeight: 800 }}>
                  {mediaCountLabel}
                </Box>
              </Box>
            ) : (
              `${t("Meetings.mediaGallery")} · ${mediaCountLabel}`
            )
          }
          sx={{
            ...headerChipSx,
            "& .MuiChip-label": { px: compactChrome ? 0.65 : 0.85 },
          }}
        />
      </Tooltip>
      <Box sx={meetingHeaderToolGroupSx(theme)}>
        <Typography
          component="span"
          variant="caption"
          sx={{
            ...meetingHeaderToolGroupLabelSx(),
            display: { xs: "none", md: "inline" },
          }}
        >
          {t("Meetings.toolGroupView")}
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={layoutMode}
          onChange={(_e, next) => {
            if (next) setLayoutMode(next);
          }}
          aria-label={t("Meetings.mediaLayoutMode")}
          sx={{
            height: 28,
            "& .MuiToggleButton-root": {
              height: 28,
              minHeight: 28,
              px: { xs: 0.65, sm: 0.85 },
              py: 0,
              fontWeight: 800,
              fontSize: "0.68rem",
              lineHeight: 1,
              textTransform: "none",
            },
          }}
        >
          <ToggleButton
            value="gallery"
            aria-label={t("Meetings.mediaLayoutGallery")}
          >
            <ViewModuleOutlinedIcon
              sx={{ fontSize: 16, mr: compactChrome ? 0 : 0.4 }}
            />
            <Box
              component="span"
              sx={{ display: { xs: "none", sm: "inline" } }}
            >
              {t("Meetings.mediaLayoutGallery")}
            </Box>
          </ToggleButton>
          <ToggleButton
            value="cards"
            aria-label={t("Meetings.mediaLayoutCards")}
          >
            <ViewAgendaOutlinedIcon
              sx={{ fontSize: 16, mr: compactChrome ? 0 : 0.4 }}
            />
            <Box
              component="span"
              sx={{ display: { xs: "none", sm: "inline" } }}
            >
              {t("Meetings.mediaLayoutCards")}
            </Box>
          </ToggleButton>
        </ToggleButtonGroup>
        {!galleryMode ? (
          <>
            <Tooltip title={t("Meetings.mediaGroupsTreeExpand")}>
              <IconButton
                onClick={() => expandAllGroups(true)}
                aria-label={t("Meetings.mediaGroupsTreeExpand")}
                size="small"
                sx={meetingHeaderToolIconBtnSx}
              >
                <UnfoldMoreOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("Meetings.mediaGroupsTreeCollapse")}>
              <IconButton
                onClick={() => expandAllGroups(false)}
                aria-label={t("Meetings.mediaGroupsTreeCollapse")}
                size="small"
                sx={meetingHeaderToolIconBtnSx}
              >
                <UnfoldLessOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ) : null}
        <Tooltip title={t("Meetings.mediaHelpOpen")}>
          <IconButton
            size="small"
            onClick={() => setHelpOpen(true)}
            aria-label={t("Meetings.mediaHelpOpen")}
            sx={meetingHeaderToolIconBtnSx}
          >
            <HelpOutlineOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {uploaderOptions.names.length || uploaderOptions.hasNone ? (
        compactChrome ? (
          <>
            <Chip
              size="small"
              icon={
                <PersonOutlineOutlinedIcon
                  sx={{ fontSize: "16px !important" }}
                />
              }
              label={
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.35,
                    maxWidth: 120,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {!uploaderFilter
                      ? t("Meetings.mediaUploaderAll")
                      : uploaderFilter === "__none__"
                        ? t("Meetings.mediaUploaderNone")
                        : uploaderFilter}
                  </Box>
                  <ExpandMoreOutlinedIcon
                    sx={{ fontSize: 16, flexShrink: 0 }}
                  />
                </Box>
              }
              color={uploaderFilter ? "primary" : "default"}
              variant={uploaderFilter ? "filled" : "outlined"}
              onClick={(e) => setUploaderMenuEl(e.currentTarget)}
              sx={{
                ...uploaderChipSx,
                maxWidth: 168,
                "& .MuiChip-label": { px: 0.75 },
              }}
              aria-label={t("Meetings.toolGroupUploader")}
            />
            <Menu
              anchorEl={uploaderMenuEl}
              open={Boolean(uploaderMenuEl)}
              onClose={() => setUploaderMenuEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              {[
                { key: "", label: t("Meetings.mediaUploaderAll") },
                ...uploaderOptions.names.map((name) => ({
                  key: name,
                  label: name,
                })),
                ...(uploaderOptions.hasNone
                  ? [
                      {
                        key: "__none__",
                        label: t("Meetings.mediaUploaderNone"),
                      },
                    ]
                  : []),
              ].map((opt) => {
                const selected = uploaderFilter === opt.key;
                return (
                  <MenuItem
                    key={opt.key || "__all__"}
                    selected={selected}
                    onClick={() => {
                      setUploaderFilter(opt.key);
                      setUploaderMenuEl(null);
                    }}
                  >
                    <ListItemText primary={opt.label} />
                    {selected ? (
                      <CheckOutlinedIcon
                        sx={{ fontSize: 18, ml: 1, color: "primary.main" }}
                      />
                    ) : null}
                  </MenuItem>
                );
              })}
            </Menu>
          </>
        ) : (
          <Box sx={meetingHeaderToolGroupSx(theme)}>
            <Typography
              component="span"
              variant="caption"
              sx={meetingHeaderToolGroupLabelSx()}
            >
              {t("Meetings.toolGroupUploader")}
            </Typography>
            <Chip
              size="small"
              label={t("Meetings.mediaUploaderAll")}
              color={!uploaderFilter ? "primary" : "default"}
              variant={!uploaderFilter ? "filled" : "outlined"}
              onClick={() => setUploaderFilter("")}
              sx={uploaderChipSx}
            />
            {uploaderOptions.names.map((name) => (
              <Chip
                key={name}
                size="small"
                label={name}
                color={uploaderFilter === name ? "primary" : "default"}
                variant={uploaderFilter === name ? "filled" : "outlined"}
                onClick={() => setUploaderFilter(name)}
                sx={uploaderChipSx}
              />
            ))}
            {uploaderOptions.hasNone ? (
              <Chip
                size="small"
                label={t("Meetings.mediaUploaderNone")}
                color={uploaderFilter === "__none__" ? "primary" : "default"}
                variant={uploaderFilter === "__none__" ? "filled" : "outlined"}
                onClick={() => setUploaderFilter("__none__")}
                sx={uploaderChipSx}
              />
            ) : null}
          </Box>
        )
      ) : null}
    </>
  );

  return (
    <Box
      sx={{
        height: { md: "100%" },
        minHeight: { md: 0 },
        display: "flex",
        flexDirection: "column",
      }}
    >
      {toolbarHost ? createPortal(mediaToolbar, toolbarHost) : mediaToolbar}
      <MeetingMediaHelpDialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
      {selectedOrderedPhotos.length ? (
        <Box sx={(th) => meetingSelectionBarSx(th)}>
          <Chip
            size="small"
            color="primary"
            label={t("Meetings.selectedMediaCountShort", {
              count: selectedOrderedPhotos.length,
            })}
            sx={{
              height: 26,
              fontWeight: 800,
              "& .MuiChip-label": { px: 0.9 },
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.15,
              display: { xs: "none", sm: "inline" },
            }}
          >
            {t("Meetings.selectedMediaBarHint")}
          </Typography>
          <Box sx={{ flex: "1 1 auto", minWidth: 8 }} />
          <Box
            sx={(th) => ({
              ...meetingHeaderToolGroupSx(th, { accent: true }),
              height: 32,
              minHeight: 32,
              maxHeight: 32,
            })}
          >
            <Tooltip
              title={t("Meetings.mediaGroupAddFromSelection", {
                count: selectedOrderedPhotos.length,
              })}
            >
              <IconButton
                size="small"
                onClick={addGroup}
                aria-label={t("Meetings.mediaGroupAddFromSelection", {
                  count: selectedOrderedPhotos.length,
                })}
                sx={{
                  ...meetingHeaderToolIconBtnSx,
                  width: 30,
                  height: 30,
                  color: "primary.main",
                }}
              >
                <CreateNewFolderOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            {namedGroupOptions.length ? (
              <>
                <Tooltip title={t("Meetings.mediaAddToGroup")}>
                  <IconButton
                    size="small"
                    onClick={(e) => setAddToGroupAnchor(e.currentTarget)}
                    aria-label={t("Meetings.mediaAddToGroup")}
                    sx={{
                      ...meetingHeaderToolIconBtnSx,
                      width: 30,
                      height: 30,
                    }}
                  >
                    <DriveFileMoveOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={addToGroupAnchor}
                  open={Boolean(addToGroupAnchor)}
                  onClose={() => setAddToGroupAnchor(null)}
                >
                  {namedGroupOptions.map((g) => (
                    <MenuItem
                      key={g.id}
                      onClick={() => {
                        moveSelectionToGroup(g.id);
                        setAddToGroupAnchor(null);
                      }}
                    >
                      <ListItemText
                        primary={g.label}
                        secondary={t("Meetings.mediaAddToGroupCount", {
                          count: g.count,
                        })}
                      />
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : null}
            <Tooltip title={t("Meetings.clearSelection")}>
              <IconButton
                size="small"
                onClick={clearSelection}
                aria-label={t("Meetings.clearSelection")}
                sx={{
                  ...meetingHeaderToolIconBtnSx,
                  width: 30,
                  height: 30,
                }}
              >
                <CloseOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ) : null}
      {uploaderFilter && viewTotal === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1.5, fontWeight: 600 }}
        >
          {t("Meetings.mediaUploaderEmpty")}
        </Typography>
      ) : null}

      {galleryMode ? (
        <Box
          sx={{
            flex: { md: 1 },
            minHeight: { md: 0, xs: "60vh" },
            display: "flex",
            flexDirection: "column",
            p: { xs: 1, sm: 1.25 },
            pb: 0,
            borderRadius: MEETING_CARD_RADIUS,
            bgcolor: "transparent",
          }}
        >
          <Box
            sx={{
              flex: { md: 1 },
              minHeight: { md: 0 },
              overflowY: { md: "auto" },
              display: "grid",
              // Móvil: 1 columna (álbum grande, fácil de tocar).
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
                xl: "repeat(5, minmax(0, 1fr))",
              },
              gap: { xs: 1.5, sm: 2 },
              alignItems: "stretch",
              alignContent: "start",
              pb: compactChrome ? `${looseDockPx + 8}px` : 1,
              WebkitOverflowScrolling: "touch",
            }}
          >
            {restColumns
              .filter((col) => !col.ungrouped)
              .map((col, index) => renderAlbumCard(col, index))}
          </Box>
          {restColumns
            .filter((col) => col.ungrouped)
            .map((col) =>
              compactChrome ? (
                <React.Fragment key={String(col.id)}>
                  {renderLooseGallery(col)}
                </React.Fragment>
              ) : (
                <Box key={String(col.id)} sx={{ flexShrink: 0 }}>
                  {renderLooseGallery(col)}
                </Box>
              ),
            )}
        </Box>
      ) : (
        <Box
          sx={{
            flex: { md: 1 },
            minHeight: { md: 0, xs: "60vh" },
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
          }}
        >
          <DragDropContext onDragEnd={onDragEnd}>
            <Stack spacing={1.5} sx={{ p: { xs: 1, sm: 1.5 }, pb: 2 }}>
              {restColumns.map((col) => renderGroupColumn(col))}
            </Stack>
          </DragDropContext>
        </Box>
      )}

      <Snackbar
        open={illustratorPasteHint}
        autoHideDuration={5000}
        onClose={() => setIllustratorPasteHint(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={copyHintSeverity}
          variant="filled"
          onClose={() => setIllustratorPasteHint(false)}
          sx={{ alignItems: "center" }}
        >
          {copyHintText || t("Meetings.mediaCopiedForIllustrator")}
        </Alert>
      </Snackbar>

      <MeetingDeleteMediaDialog
        open={Boolean(deleteTarget)}
        onClose={() => !deletingMedia && setDeleteTarget(null)}
        onConfirm={confirmDeleteMedia}
        deleting={deletingMedia}
        isVideo={Boolean(deleteTargetIsVideo)}
        mediaId={deleteTarget?.id}
      />

      <Dialog
        open={Boolean(deleteGroupTarget)}
        onClose={() => setDeleteGroupTarget(null)}
        fullWidth
        maxWidth="xs"
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(10px)",
              backgroundColor: "rgba(15, 23, 42, 0.45)",
            },
          },
        }}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        }}
      >
        <DialogTitle>{t("Meetings.mediaGroupRemoveConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Meetings.mediaGroupRemoveConfirmBody", {
              name:
                String(deleteGroupTarget?.label || "").trim() ||
                deleteGroupTarget?.id ||
                "",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteGroupTarget(null)}>
            {t("Meetings.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmRemoveGroup}
            startIcon={<DeleteOutlineOutlinedIcon />}
          >
            {t("Meetings.mediaGroupRemoveConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});
